import React, { useEffect, useState } from 'react'
import type { GraphData } from '../components/LineGraph'
import { observer } from 'mobx-react'
import Spinner from '../components/Spinner'
import Treasury from '../state/Treasury'
import { RADOM_COLORS, WINDOW_SIZE } from '../util/Constants'
import { TrendNeutral, Trend } from '../components/TrendCookie'
import { format, formatDistance, subDays } from 'date-fns'
import styled from 'styled-components'
import Analytics from '../state/Analytics'
import Tooltip from '../components/Tooltip'
import Events, { AssociatedEntityType } from '../state/Events'
import { useLocation, useNavigate } from 'react-router-dom'
import { errorToast, formatCurrency, useTestMode } from '../util/Util'
import { Chevron } from '../icons/Chevron'
import { Container } from '../components/Animations'
import LoadingBar from '../components/LoadingBar'
import { Tokens } from '../util/Tokens'
import { uniq } from 'lodash'
import { getMethod, getMethodLogoByTicker, prodNetworks, testNetworks } from '../util/Managed'
import User from '../state/User'
import VerificationBanner from '../components/VerificationBanner'
import Modal from '../components/Modal'
import Radom, {
  BridgeVirtualAccountCapabilities,
  Customer,
  DepositAccount,
  DepositAccountType,
  IOrganization,
  IProduct
} from '../state/Radom'
import { PrimaryButton } from '../components/Button'
import { SummaryLabel, SummaryMeta, SummaryStrip, SummaryStripItem, SummaryValue } from '../components/DashboardPrimitives'
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis
} from 'recharts'
import type { RadomEvent } from '../state/Events'

const EmptyData = {
  curPeriod: Array(7).fill(0).map((_, i) => ({ x: subDays(new Date(), i).getTime(), y: 0 })).reverse(),
  prevPeriod: Array(7).fill(0).map((_, i) => ({ x: subDays(new Date(), i).getTime(), y: 0 })).reverse()
}

const TIME_RANGE_OPTIONS = [
  { label: '7D', title: 'Last 7 days', numDays: 14 },
  { label: '30D', title: 'Last 30 days', numDays: 60 },
  { label: '90D', title: 'Last 90 days', numDays: 180 },
  { label: '6M', title: 'Last 6 months', numDays: 365 },
  { label: '1Y', title: 'Last year', numDays: 730 }
]

const MAIN_CHART_OPTIONS = [
  { label: 'Revenue', value: 'revenue' },
  { label: 'Payments', value: 'payments' },
  { label: 'Conversion', value: 'conversion' }
] as const

type MainChartMode = typeof MAIN_CHART_OPTIONS[number]['value']
const DASHBOARD_TABS = [
  { label: 'Overview', value: 'overview' },
  { label: 'Payments', value: 'payments' },
  { label: 'Products', value: 'products' },
  { label: 'Customers', value: 'customers' },
  { label: 'Banking', value: 'banking' }
] as const

type DashboardTab = typeof DASHBOARD_TABS[number]['value']
type HomeMode = 'home' | 'analytics'

const HOME_BALANCE_TOKENS = ['BTC', 'USDC', 'BNB', 'USDT', 'ETH', 'SOL', 'TRX', 'LTC', 'BCH']
const HOME_BALANCE_PRIMARY_LIMIT = 6
const HOME_HERO_CARD_HEIGHT = 304

const CHART_COLORS = {
  revenue: '#3464e6',
  payments: '#15957b',
  activity: '#0f766e',
  checkout: '#7c3aed',
  product: '#f59e0b',
  customers: '#be185d',
  banking: '#0891b2',
  subscriptions: '#64748b'
}

type ActivityTone = 'green' | 'red' | 'blue' | 'neutral'

interface RecentActivityItem {
  id: string
  title: string
  meta: string
  amountLabel: string
  whenLabel: string
  tone: ActivityTone
  url: string
}

const QA_ASSET_ROWS = [
  { token: 'BTC', balance: 2.4532 },
  { token: 'USDC', balance: 124860.42 },
  { token: 'BNB', balance: 92.74 },
  { token: 'USDT', balance: 32123.45 },
  { token: 'ETH', balance: 15.234 },
  { token: 'SOL', balance: 384.12 },
  { token: 'TRX', balance: 68420.3 },
  { token: 'LTC', balance: 126.8 },
  { token: 'BCH', balance: 0 }
]

const QA_TOP_CUSTOMERS: Customer[] = [
  {
    id: 'qa-customer-northstar',
    totalSpend: '42860.55',
    email: 'finance@northstar.example',
    name: 'Northstar Supply Co.',
    billingAddress: '27 Market Street, London',
    residenceCountry: 'GB',
    payments: [],
    tags: ['identity_verified', 'enterprise'],
    createdAt: subDays(new Date(), 94).toISOString(),
    accountType: 'Business',
    companyName: 'Northstar Supply Co.'
  },
  {
    id: 'qa-customer-keedslermotors',
    totalSpend: '31980.20',
    email: 'ap@keedsler.example',
    name: 'Keedsler Motors',
    residenceCountry: 'US',
    payments: [],
    tags: ['identity_verified'],
    createdAt: subDays(new Date(), 72).toISOString(),
    accountType: 'Business',
    companyName: 'Keedsler Motors'
  },
  {
    id: 'qa-customer-ziffcorp',
    totalSpend: '18440.00',
    email: 'payments@ziffcorp.example',
    name: 'ZiffCorp',
    residenceCountry: 'IE',
    payments: [],
    tags: ['recurring'],
    createdAt: subDays(new Date(), 48).toISOString(),
    accountType: 'Business',
    companyName: 'ZiffCorp'
  },
  {
    id: 'qa-customer-jane-doe',
    totalSpend: '7600.90',
    email: 'jane.doe@example.com',
    name: 'Jane Doe',
    residenceCountry: 'US',
    payments: [],
    tags: [],
    createdAt: subDays(new Date(), 21).toISOString(),
    accountType: 'Individual',
    companyName: ''
  }
]

const QA_RECENT_PRODUCTS: IProduct[] = [
  {
    id: 'qa-product-launch-plan',
    name: 'Launch plan',
    description: 'One-time onboarding package.',
    addOns: [],
    currency: 'GBP',
    price: 100,
    createdAt: subDays(new Date(), 38).toISOString(),
    updatedAt: subDays(new Date(), 2).toISOString(),
    imageUrl: '',
    isArchived: false
  },
  {
    id: 'qa-product-priority-support',
    name: 'Priority support',
    description: 'Monthly support retainer.',
    addOns: [],
    chargingIntervalSeconds: 2592000,
    currency: 'GBP',
    price: 49,
    createdAt: subDays(new Date(), 66).toISOString(),
    updatedAt: subDays(new Date(), 5).toISOString(),
    imageUrl: 'https://uploads-ssl.webflow.com/635182914b66e31f085ba568/635182914b66e3594c5ba575_Radom%20Full%20Logo.svg',
    isArchived: false
  },
  {
    id: 'qa-product-platform-access',
    name: 'Platform access',
    description: '',
    addOns: [],
    currency: 'GBP',
    price: 1200,
    createdAt: subDays(new Date(), 90).toISOString(),
    updatedAt: subDays(new Date(), 11).toISOString(),
    imageUrl: '',
    isArchived: false
  }
]

const QA_PRODUCT_REVENUE_BY_ID: Record<string, number> = {
  'qa-product-launch-plan': 41200,
  'qa-product-priority-support': 18640,
  'qa-product-platform-access': 13200
}

const QA_VIRTUAL_ACCOUNTS: DepositAccount[] = [
  {
    id: 'va_usd_operating',
    organizationId: 'qa-org',
    name: 'USD operating account',
    status: 'active',
    createdAt: subDays(new Date(), 41).toISOString(),
    accountDetails: {
      Fiat: {
        bankName: 'US banking partner',
        bankAddress: 'New York, NY',
        accountType: {
          us: {
            accountNumber: '1234567890',
            routingNumber: '021000021',
            sourceCurrency: 'USD',
            sourcePaymentRails: ['ach_push', 'wire'],
            destinationCurrency: 'USDC',
            destinationPaymentRail: 'ethereum'
          }
        }
      }
    }
  },
  {
    id: 'va_eur_settlement',
    organizationId: 'qa-org',
    name: 'EUR settlement account',
    status: 'active',
    createdAt: subDays(new Date(), 18).toISOString(),
    accountDetails: {
      Fiat: {
        bankName: 'EU banking partner',
        bankAddress: 'Dublin, IE',
        accountType: {
          iban: {
            iban: 'IE29AIBK93115212345678',
            bic: 'AIBKIE2D',
            sourceCurrency: 'EUR',
            sourcePaymentRails: ['sepa'],
            destinationCurrency: 'USDC',
            destinationPaymentRail: 'base'
          }
        }
      }
    }
  }
]

const QA_RECENT_ACTIVITY: RecentActivityItem[] = [
  {
    id: 'qa-activity-invoice',
    title: 'Invoice payment',
    meta: 'Northstar Supply Co. paid invoice INV-2026-014',
    amountLabel: '$12,400',
    whenLabel: '14 minutes ago',
    tone: 'green',
    url: '/invoices'
  },
  {
    id: 'qa-activity-checkout',
    title: 'Checkout session completed',
    meta: 'Open banking payment from Keedsler Motors',
    amountLabel: '$5,480',
    whenLabel: '46 minutes ago',
    tone: 'green',
    url: '/checkout_sessions'
  },
  {
    id: 'qa-activity-deposit',
    title: 'Virtual account deposit',
    meta: 'EUR settlement account received a fiat deposit',
    amountLabel: '$8,200',
    whenLabel: '2 hours ago',
    tone: 'green',
    url: '/virtual_accounts'
  },
  {
    id: 'qa-activity-payout',
    title: 'Payout sent',
    meta: 'Off-ramp payout to Northstar Supply Co.',
    amountLabel: '-$3,850',
    whenLabel: '5 hours ago',
    tone: 'red',
    url: '/payouts'
  },
  {
    id: 'qa-activity-payment-link',
    title: 'Payment link payment',
    meta: 'Launch plan purchased via payment link',
    amountLabel: '$100',
    whenLabel: 'Yesterday',
    tone: 'green',
    url: '/payment_links'
  }
]

const getEmptyDataForRange = (numDays: number): { curPeriod: Point[], prevPeriod: Point[] } => {
  const safeNumDays = Math.max(numDays, 2)
  const data = Array(safeNumDays).fill(0).map((_, i) => ({
    x: subDays(new Date(), safeNumDays - i - 1).getTime(),
    y: 0
  }))
  const midpoint = Math.floor(data.length / 2)
  return {
    prevPeriod: data.slice(0, midpoint),
    curPeriod: data.slice(midpoint)
  }
}

const getQaMetricData = (metricName: string, numDays: number): GraphData => {
  const days = Math.max(numDays, 2)
  const start = subDays(new Date(), days - 1)

  return Array(days).fill(0).map((_, idx) => {
    const x = subDays(start, -idx).getTime()
    const progress = idx / Math.max(days - 1, 1)
    const wave = Math.sin(idx / 3.1) + Math.cos(idx / 6.4)
    const weekly = Math.sin(idx / 1.8)
    let y = 0

    switch (metricName) {
    case 'Gross revenue':
    case 'Gross volume':
      y = 11200 + progress * 6200 + wave * 900 + weekly * 420
      break
    case 'Net volume':
      y = (11200 + progress * 6200 + wave * 900 + weekly * 420) * 0.982
      break
    case 'One-time payments':
      y = 42 + progress * 22 + wave * 4
      break
    case 'Recurring payments':
      y = 6 + progress * 5 + weekly * 1.2
      break
    case 'Average payment size':
      y = 168 + progress * 28 + wave * 11
      break
    case 'Total subscribers':
      y = 314 + progress * 34 + wave * 3
      break
    case 'New subscriptions':
      y = 2 + progress * 4 + Math.max(0, weekly * 2)
      break
    case 'Subscription cancellations':
      y = 1 + Math.max(0, Math.sin(idx / 4.6) * 1.4)
      break
    case 'Subscription expirations':
      y = 0.5 + Math.max(0, Math.cos(idx / 7.2) * 0.8)
      break
    case 'Subscription churn':
      y = 2.6 + Math.sin(idx / 5.2) * 0.7 - progress * 0.3
      break
    case 'Crypto checkout conversion':
      y = 61 + progress * 7 + wave * 2.2
      break
    case 'Open banking conversion':
      y = 78 + progress * 5 + weekly * 1.6
      break
    case 'Crypto checkout starts':
      y = 92 + progress * 33 + Math.max(0, wave * 7)
      break
    case 'Open banking checkout starts':
      y = 64 + progress * 22 + Math.max(0, weekly * 5)
      break
    case 'Crypto completed checkouts':
      y = 58 + progress * 24 + Math.max(0, wave * 5)
      break
    case 'Open banking completed checkouts':
      y = 51 + progress * 19 + Math.max(0, weekly * 4)
      break
    case 'Invoice revenue':
      y = 9100 + progress * 2400 + Math.max(0, wave * 860)
      break
    case 'Payment link revenue':
      y = 13200 + progress * 3900 + Math.max(0, weekly * 1320)
      break
    case 'Checkout session revenue':
      y = 15100 + progress * 4700 + Math.max(0, wave * 1080)
      break
    case 'Payment session revenue':
      y = 6400 + progress * 1900 + Math.max(0, Math.sin(idx / 2.4) * 820)
      break
    case 'Deposit address volume':
      y = 8200 + progress * 2600 + Math.max(0, Math.cos(idx / 4.2) * 900)
      break
    case 'Open banking payments':
      y = 16 + progress * 9 + Math.max(0, weekly * 3.2)
      break
    case 'Open banking volume':
      y = 6200 + progress * 4100 + wave * 560
      break
    case 'Virtual account deposits':
      y = 8200 + progress * 2600 + Math.max(0, weekly * 900)
      break
    case 'Off-ramp volume':
      y = 4700 + progress * 3200 + Math.max(0, Math.sin(idx / 3.8) * 760)
      break
    default:
      y = 0
    }

    const integerMetric = [
      'One-time payments',
      'Recurring payments',
      'Total subscribers',
      'New subscriptions',
      'Subscription cancellations',
      'Subscription expirations',
      'Crypto checkout starts',
      'Open banking checkout starts',
      'Crypto completed checkouts',
      'Open banking completed checkouts',
      'Open banking payments'
    ].includes(metricName)

    return {
      x,
      y: integerMetric
        ? Math.max(0, Math.round(y))
        : Math.max(0, Number(y.toFixed(2)))
    }
  })
}

const SectionPanel = styled.section<{ $accentColor?: string }>`
  border: 1px solid #e7ebf2;
  border-radius: 16px;
  background: #fff;
  box-sizing: border-box;
  padding: 13px;
  position: relative;
  overflow: hidden;

  @media (max-width: ${WINDOW_SIZE.MOBILE_LARGE}) {
    padding: 14px;
  }
`

const SectionHeader = styled.div`
  margin-bottom: 8px;
  display: flex;
  gap: 12px;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
`

const SectionTitle = styled.div`
  color: #20242d;
  font-size: 17px;
  font-weight: 500;
`

const SectionSubtext = styled.div`
  color: #727888;
  font-size: 12px;
  margin-top: 2px;
`

const OverviewHeading = styled.h1`
  color: #20242d;
  font-size: 22px;
  font-weight: 500;
  letter-spacing: -0.02em;
  line-height: 1.1;
  margin: 0;
`

const HomeSectionHeading = styled.h2`
  color: #151922;
  font-size: 15px;
  font-weight: 500;
  letter-spacing: -0.01em;
  line-height: 1.2;
  margin: 0;
`

const HomeSectionSubtext = styled.p`
  color: #6f7888;
  font-size: 12.5px;
  line-height: 1.45;
  margin: 5px 0 0;
  max-width: 38ch;
`

const ControlRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: 8px;
`

const AnalyticsTopBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
`

const HomeTabs = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  padding: 4px;
  border: 1px solid #e3e7ef;
  border-radius: 14px;
  background: #f7f9fc;
  width: fit-content;
`

const HomeTabButton = styled.button<{ $active?: boolean }>`
  border: 0;
  border-radius: 10px;
  background: ${props => props.$active ? '#fff' : 'transparent'};
  color: ${props => props.$active ? '#111827' : '#6f7686'};
  cursor: pointer;
  font-family: var(--font-sans);
  font-size: 12.5px;
  font-weight: 500;
  padding: 7px 11px;
  transition: 0.16s ease background-color, 0.16s ease color;

  &:hover {
    background: ${props => props.$active ? '#fff' : '#eef2f8'};
    color: #111827;
  }
`

const HomeHeroGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 436px) minmax(0, 1fr);
  gap: 24px;
  align-items: start;

  @media (max-width: 1100px) {
    grid-template-columns: 1fr;
  }
`

const HomeSecondaryGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 14px;
  align-items: start;
`

const ActivityList = styled.div`
  border: 1px solid #edf1f7;
  border-radius: 14px;
  overflow: hidden;
  max-height: 320px;
  overflow-y: auto;

  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-thumb {
    background: #d8dee9;
    border-radius: 999px;
  }
`

const HomeActivityEmptyState = styled.div`
  background: #f8fafc;
  border: 1px solid #edf1f7;
  border-radius: 14px;
  color: #6f7888;
  font-size: 13px;
  line-height: 1.45;
  padding: 14px 16px;
`

const AnalyticsMetricsSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`

const AnalyticsMetricsGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const AnalyticsGroupLabel = styled.div`
  color: #4f5667;
  font-size: 11.5px;
  font-weight: 500;
  letter-spacing: 0.04em;
  text-transform: uppercase;
`

const ActivityRow = styled.button`
  align-items: center;
  background: #fff;
  border: 0;
  border-bottom: 1px solid #edf1f7;
  color: inherit;
  cursor: pointer;
  display: grid;
  gap: 12px;
  grid-template-columns: auto minmax(0, 1fr) auto;
  padding: 11px 13px;
  text-align: left;
  transition: 0.16s ease background-color;
  width: 100%;

  &:hover {
    background: #f8fafc;
  }

  &:last-child {
    border-bottom: 0;
  }

  @media (max-width: ${WINDOW_SIZE.MOBILE_LARGE}) {
    grid-template-columns: auto minmax(0, 1fr);
  }
`

const ActivityBadge = styled.div<{ $tone?: 'green' | 'red' | 'blue' | 'neutral' }>`
  align-items: center;
  background: ${({ $tone = 'neutral' }) => {
    switch ($tone) {
    case 'green':
      return '#e8f7ef'
    case 'red':
      return '#ffe9ec'
    case 'blue':
      return '#edf3ff'
    default:
      return '#f3f5f9'
    }
  }};
  border-radius: 12px;
  color: ${({ $tone = 'neutral' }) => {
    switch ($tone) {
    case 'green':
      return '#11724f'
    case 'red':
      return '#c51735'
    case 'blue':
      return '#3157c6'
    default:
      return '#687183'
    }
  }};
  display: inline-flex;
  font-size: 12px;
  font-weight: 600;
  height: 30px;
  justify-content: center;
  min-width: 30px;
  padding: 0 9px;
`

const ActivityTitle = styled.div`
  color: #20242d;
  font-size: 13.5px;
  font-weight: 500;
  line-height: 1.3;
`

const ActivityMeta = styled.div`
  color: #727888;
  font-size: 12px;
  line-height: 1.45;
  margin-top: 3px;
`

const ActivityAside = styled.div`
  align-items: flex-end;
  display: flex;
  flex-direction: column;
  gap: 3px;

  @media (max-width: ${WINDOW_SIZE.MOBILE_LARGE}) {
    grid-column: 2;
    align-items: flex-start;
  }
`

const ActivityAmount = styled.div<{ $tone?: 'green' | 'red' | 'blue' | 'neutral' }>`
  color: ${({ $tone = 'neutral' }) => {
    switch ($tone) {
    case 'green':
      return '#11724f'
    case 'red':
      return '#c51735'
    case 'blue':
      return '#3157c6'
    default:
      return '#20242d'
    }
  }};
  font-size: 13.5px;
  font-weight: 500;
`

const ActivityWhen = styled.div`
  color: #8b92a2;
  font-size: 11.5px;
`

const MerchantHeader = styled.section<{ $needsAttention?: boolean }>`
  align-items: center;
  background: ${({ $needsAttention }) => $needsAttention ? '#fff7ed' : 'transparent'};
  border: 1px solid ${({ $needsAttention }) => $needsAttention ? '#fed7aa' : 'transparent'};
  border-radius: ${({ $needsAttention }) => $needsAttention ? '14px' : '0'};
  display: grid;
  gap: 8px 14px;
  grid-template-columns: minmax(0, 1fr) auto;
  padding: ${({ $needsAttention }) => $needsAttention ? '10px 12px' : '2px 0'};

  @media (max-width: ${WINDOW_SIZE.TABLET}) {
    align-items: flex-start;
    grid-template-columns: 1fr;
  }
`

const WelcomeProfile = styled.div`
  align-items: center;
  display: grid;
  gap: 8px;
  grid-template-columns: auto minmax(0, 1fr);
  min-width: 0;
`

const WelcomeLogo = styled.div<{ $imageUrl?: string }>`
  align-items: center;
  background: ${({ $imageUrl }) => $imageUrl ? `#f7f9fc url(${$imageUrl}) center / contain no-repeat` : '#eef2ff'};
  border: 1px solid #e3e7ef;
  border-radius: 12px;
  color: #4f46e5;
  display: flex;
  font-size: 14px;
  font-weight: 500;
  height: 38px;
  justify-content: center;
  overflow: hidden;
  width: 38px;

  @media (max-width: ${WINDOW_SIZE.MOBILE_LARGE}) {
    height: 36px;
    width: 36px;
  }
`

const WelcomeTitleRow = styled.div`
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  min-width: 0;
`

const WelcomeStatusRow = styled.div`
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`

const WelcomeTitle = styled.div`
  color: #20242d;
  font-size: 15px;
  font-weight: 500;
  letter-spacing: -0.01em;
  line-height: 1.2;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const WelcomeMetaRow = styled.div`
  color: #6f7888;
  display: flex;
  flex-wrap: wrap;
  font-size: 12px;
  gap: 0;
  line-height: 1.5;
  margin-top: 3px;

  > span {
    color: #687183;
    font-weight: 400;
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  > span:first-child {
    color: #566072;
    font-weight: 500;
  }

  > span + span::before {
    color: #adb5c3;
    content: '·';
    margin: 0 7px;
  }
`

const WelcomeHelperText = styled.div`
  color: #6f7888;
  font-size: 12px;
  line-height: 1.45;
  margin-top: 5px;
  max-width: 64ch;
`

const OverviewHeaderRow = styled.div`
  align-items: center;
  display: flex;
  gap: 10px;
  margin-top: 2px;
`

const AnalyticsToolbarTitle = styled.div`
  color: #252b36;
  font-size: 14px;
  font-weight: 500;
`

const AnalyticsToolbarSubtext = styled.div`
  color: #7d8492;
  font-size: 12px;
  margin-top: 2px;
`

const ExpandedChartControls = styled.div`
  align-items: center;
  background: #f8fafc;
  border: 1px solid #e3e7ef;
  border-radius: 14px;
  display: flex;
  gap: 12px;
  justify-content: space-between;
  margin-bottom: 16px;
  padding: 10px 12px;

  @media (max-width: ${WINDOW_SIZE.MOBILE_LARGE}) {
    align-items: flex-start;
    flex-direction: column;
  }
`

const SetupStatus = styled.div<{ $isComplete?: boolean }>`
  align-items: center;
  background: ${props => props.$isComplete ? '#e4f7ee' : '#fff7ed'};
  border-radius: 999px;
  color: ${props => props.$isComplete ? '#11724f' : '#b45309'};
  display: inline-flex;
  font-size: 11px;
  font-weight: 500;
  padding: 4px 8px;
`

const SetupAction = styled.button`
  align-self: flex-start;
  align-items: center;
  border: 1px solid #d9dee8;
  border-radius: 12px;
  background: #fff;
  color: #313745;
  cursor: pointer;
  display: inline-flex;
  flex: 0 0 auto;
  gap: 8px;
  font-family: var(--font-sans);
  font-size: 13px;
  font-weight: 500;
  min-height: 40px;
  padding: 0 15px;
  transition: 0.16s ease background-color, 0.16s ease border-color, 0.16s ease box-shadow;
  white-space: nowrap;

  svg:last-child {
    transition: transform 0.16s ease, opacity 0.16s ease;
  }

  &:hover {
    background: #f7f9fc;
    border-color: #cbd3e1;
    box-shadow: 0 8px 18px rgba(17, 17, 17, 0.04);
  }

  &:hover svg:last-child {
    transform: translateX(2px) rotate(-90deg);
  }
`

const ProfileActionButton = styled(SetupAction)`
  min-height: 34px;
  padding: 0 12px;
`

const ActionButtonText = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
`

const ActionButtonArrow = styled(Chevron)`
  height: auto;
  min-width: 10px;
  opacity: 0.78;
  transform: rotate(-90deg);
  transition: transform 0.16s ease, opacity 0.16s ease;
`

const EmptySection = styled.div`
  border: 1px dashed #d9dee8;
  border-radius: 9px;
  color: #727888;
  flex: 0 0 auto;
  font-size: 14px;
  line-height: 1.5;
  padding: 18px;
`

const EntityList = styled.div`
  border: 1px solid #e3e7ef;
  border-radius: 9px;
  overflow: hidden;
`

const ProductRevenueChart = styled.div`
  border: 1px solid #e3e7ef;
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 16px;
  padding: 14px;
`

const ProductRevenueChartHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
`

const ProductRevenueChartTitle = styled.div`
  color: #20242d;
  font-size: 14px;
  font-weight: 500;
`

const ProductRevenueChartHint = styled.div`
  color: #7d8492;
  font-size: 12px;
  line-height: 1.45;
`

const ProductRevenueChartRows = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`

const ProductRevenueChartRow = styled.div`
  align-items: center;
  display: grid;
  gap: 10px;
  grid-template-columns: minmax(0, 170px) minmax(0, 1fr) auto;

  @media (max-width: ${WINDOW_SIZE.MOBILE_LARGE}) {
    grid-template-columns: 1fr;
    gap: 6px;
  }
`

const ProductRevenueChartName = styled.div`
  color: #313745;
  font-size: 13px;
  font-weight: 500;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const ProductRevenueChartTrack = styled.div`
  background: #eef2f8;
  border-radius: 999px;
  height: 8px;
  overflow: hidden;
  width: 100%;
`

const ProductRevenueChartFill = styled.div<{ $width: number }>`
  background: linear-gradient(90deg, #111827 0%, #3b82f6 100%);
  border-radius: inherit;
  height: 100%;
  width: ${({ $width }) => `${$width}%`};
`

const ProductRevenueChartValue = styled.div`
  color: #20242d;
  font-size: 13px;
  font-weight: 500;
  white-space: nowrap;
`

const EntityListHeader = styled.div`
  align-items: center;
  background: #f8fafc;
  border-bottom: 1px solid #e3e7ef;
  color: #7d8492;
  display: grid;
  font-size: 11px;
  font-weight: 500;
  gap: 12px;
  grid-template-columns: auto minmax(0, 1fr) auto;
  letter-spacing: 0.04em;
  padding: 9px 14px;
  text-transform: uppercase;

  @media (max-width: ${WINDOW_SIZE.MOBILE_LARGE}) {
    display: none;
  }
`

const EntityRow = styled.button`
  align-items: center;
  background: #fff;
  border: 0;
  border-bottom: 1px solid #edf1f7;
  color: inherit;
  cursor: pointer;
  display: grid;
  font-family: var(--font-sans);
  grid-template-columns: auto minmax(0, 1fr) auto;
  gap: 12px;
  padding: 13px 14px;
  text-align: left;
  transition: 0.16s ease background-color;
  width: 100%;

  &:last-child {
    border-bottom: 0;
  }

  &:hover {
    background: #f8fafc;
  }

  @media (max-width: ${WINDOW_SIZE.MOBILE_LARGE}) {
    grid-template-columns: auto minmax(0, 1fr);
  }
`

const EntityAvatar = styled.div<{ $imageUrl?: string }>`
  align-items: center;
  background: ${({ $imageUrl }) => $imageUrl ? `#f7f9fc url(${$imageUrl}) center / cover no-repeat` : '#eef2ff'};
  border: 1px solid #e3e7ef;
  border-radius: 50%;
  color: #4f46e5;
  display: flex;
  flex: 0 0 auto;
  font-size: 12px;
  font-weight: 500;
  height: 34px;
  justify-content: center;
  overflow: hidden;
  width: 34px;
`

const EntityTitle = styled.div`
  color: #20242d;
  font-size: 14px;
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const EntitySubtext = styled.div`
  color: #7d8492;
  font-size: 12px;
  margin-top: 3px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const EntityValue = styled.div`
  color: #20242d;
  font-size: 14px;
  font-weight: 500;
  white-space: nowrap;

  @media (max-width: ${WINDOW_SIZE.MOBILE_LARGE}) {
    grid-column: 2;
    justify-self: flex-start;
  }
`

const EntityValueLabel = styled.div`
  color: #8b92a2;
  display: none;
  font-size: 11px;
  font-weight: 500;
  letter-spacing: 0.04em;
  margin-bottom: 2px;
  text-transform: uppercase;

  @media (max-width: ${WINDOW_SIZE.MOBILE_LARGE}) {
    display: block;
  }
`

const InlinePill = styled.span<{ $tone?: 'green' | 'amber' | 'neutral' }>`
  background: ${({ $tone }) => $tone === 'green' ? '#e4f7ee' : $tone === 'amber' ? '#fff7ed' : '#eef2f8'};
  border-radius: 999px;
  color: ${({ $tone }) => $tone === 'green' ? '#11724f' : $tone === 'amber' ? '#b45309' : '#657083'};
  display: inline-flex;
  font-size: 11px;
  font-weight: 500;
  margin-left: 6px;
  padding: 4px 8px;
`

const SegmentedControl = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 2px;
  padding: 3px;
  border: 1px solid #e3e7ef;
  border-radius: 11px;
  background: #f7f9fc;
`

const SegmentButton = styled.button<{ $active?: boolean, $prominent?: boolean }>`
  border: 0;
  border-radius: 9px;
  background: ${props => props.$active ? '#fff' : 'transparent'};
  color: ${props => props.$active ? '#10141f' : '#6f7686'};
  cursor: pointer;
  font-size: ${props => props.$prominent ? '12px' : '11.5px'};
  font-weight: ${props => props.$prominent ? 600 : 500};
  line-height: 1;
  min-width: ${props => props.$prominent ? '54px' : '40px'};
  padding: ${props => props.$prominent ? '7px 11px' : '7px 10px'};
  transition: background-color 0.16s ease, color 0.16s ease;

  &:hover {
    color: #10141f;
    background: ${props => props.$active ? '#fff' : '#eef2f8'};
  }
`

const SegmentedControlLabel = styled.span`
  color: #7d8492;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.05em;
  text-transform: uppercase;
`

const MetricCard = styled.div<{ $accentColor?: string }>`
  display: flex;
  flex-direction: column;
  width: 100%;
  min-width: 0;
  background: #fff;
  padding: 10px 11px;
`

const MainMetricCard = styled(MetricCard)`
  min-height: 100%;
  border: 0;
  box-shadow: none;
  padding: 0;
`

const MetricHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 6px;
  min-width: 0;
`

const MetricName = styled.div`
  color: #313745;
  font-size: 13px;
  font-weight: 500;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  min-width: 0;
  row-gap: 4px;
`

const MetricUnit = styled.span`
  color: #8b92a2;
  font-weight: 400;
  margin-left: 5px;
`

const MetricValueRow = styled.div`
  display: flex;
  align-items: flex-end;
  flex-wrap: wrap;
  gap: 7px;
  min-height: 36px;
  min-width: 0;
  row-gap: 6px;
`

const MetricValue = styled.span<{ $large?: boolean }>`
  color: #10141f;
  font-size: ${props => props.$large ? '27px' : '20px'};
  font-weight: 500;
  line-height: 1.1;
  max-width: 100%;
  overflow-wrap: anywhere;
`

const MetricPrevious = styled.div`
  color: #8b92a2;
  font-size: 12px;
  margin-top: 6px;
`

const ChartShell = styled.div<{ $height: number, $marginTop?: number }>`
  position: relative;
  width: 100%;
  height: ${props => props.$height}px;
  margin-top: ${props => props.$marginTop ?? 12}px;
`

const ChartLoading = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
`

const ChartActionButton = styled.button`
  align-items: center;
  background: #fff;
  border: 1px solid #e3e7ef;
  border-radius: 12px;
  color: #7d8492;
  cursor: pointer;
  display: flex;
  height: 28px;
  justify-content: center;
  min-width: 28px;
  padding: 0;
  transition: 0.16s ease background-color, 0.16s ease border-color, 0.16s ease color;

  &:hover {
    background: #f7f9fc;
    border-color: #cbd3e1;
    color: #313745;
  }
`

const ExpandIcon = (props: React.SVGProps<SVGSVGElement>): React.ReactElement => (
  <svg viewBox="0 0 20 20" fill="none" aria-hidden="true" {...props}>
    <path d="M7.2 3.8H3.8V7.2" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M3.8 3.8L8 8" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M12.8 3.8H16.2V7.2" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M16.2 3.8L12 8" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M7.2 16.2H3.8V12.8" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M3.8 16.2L8 12" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M12.8 16.2H16.2V12.8" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M16.2 16.2L12 12" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
)

const BalanceActionButton = styled(PrimaryButton)`
  font-size: 13.75px;
  margin-top: 8px;
  min-height: 47px;
  padding: 0 16px;
  background: #171b24;
  box-shadow: 0 4px 12px rgba(17, 17, 17, 0.07);
  transition: background-color 0.14s ease, box-shadow 0.14s ease, transform 0.14s ease;
  width: 100%;

  &:hover {
    background: #1c2029;
    box-shadow: 0 7px 16px rgba(17, 17, 17, 0.09);
    transform: translateY(-0.5px);

    ${ActionButtonArrow} {
      opacity: 1;
      transform: translateX(2px) rotate(-90deg);
    }
  }

  &:active {
    background: #161a22;
    box-shadow: 0 2px 6px rgba(17, 17, 17, 0.07);
    transform: translateY(0.5px);

    ${ActionButtonArrow} {
      transform: translateX(1px) rotate(-90deg);
    }
  }

  &:focus-visible {
    box-shadow: 0 0 0 4px rgba(23, 27, 36, 0.14), 0 10px 22px rgba(17, 17, 17, 0.12);
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;

    ${ActionButtonArrow} {
      transition: none;
    }

    &:hover,
    &:active {
      transform: none;
    }
  }
`

const BalanceSectionPanel = styled(SectionPanel)`
  border-color: #e3e7ef;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  height: ${HOME_HERO_CARD_HEIGHT}px;
  justify-self: start;
  max-width: 436px;
  padding: 16px;
  width: 100%;

  ${SectionHeader} {
    margin-bottom: 12px;
  }

  ${HomeSectionHeading} {
    color: #2b3240;
    font-size: 14px;
    font-weight: 600;
    letter-spacing: 0;
  }

  @media (max-width: ${WINDOW_SIZE.TABLET}) {
    height: auto;
    min-height: 0;
  }

  @media (max-width: ${WINDOW_SIZE.MOBILE_LARGE}) {
    padding: 15px;
  }
`

const HomePulsePanel = styled(SectionPanel)`
  border-color: #e3e7ef;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  height: ${HOME_HERO_CARD_HEIGHT}px;
  min-width: 0;
  padding: 12px;
  width: 100%;

  ${SectionHeader} {
    margin-bottom: 5px;
  }

  ${HomeSectionSubtext} {
    margin-top: 2px;
  }

  @media (max-width: ${WINDOW_SIZE.TABLET}) {
    height: auto;
    min-height: 0;
  }

  @media (max-width: ${WINDOW_SIZE.MOBILE_LARGE}) {
    padding: 12px;
  }
`

const BalanceInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
  width: 100%;

  @media (max-width: ${WINDOW_SIZE.MOBILE_LARGE}) {
    width: 100%;
  }
`

const BalanceHeadingRow = styled.div`
  align-items: center;
  display: inline-flex;
  flex-wrap: wrap;
  gap: 4px;
  line-height: 1.2;
`

const BalanceHeadingLabel = styled.span`
  color: #20242d;
  font-size: 15.5px;
  font-weight: 400;
`

const BalanceHeadingMeta = styled.span`
  color: #7b8392;
  font-size: 15.5px;
  font-weight: 300;
`

const BalanceAssets = styled.div`
  border-top: 1px solid #edf1f7;
  display: flex;
  flex-direction: column;
  gap: 0;
  min-width: 0;
  padding-top: 14px;
`

const TokenGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 20px 34px;
  justify-content: start;
  min-width: 0;
  padding: 0;

  @media (max-width: ${WINDOW_SIZE.TABLET}) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (max-width: ${WINDOW_SIZE.MOBILE_LARGE}) {
    grid-template-columns: 1fr;
  }
`

const TokenRow = styled.div<{ $muted?: boolean }>`
  display: flex;
  align-items: center;
  column-gap: 10px;
  flex-grow: 1;
  min-width: 0;
  opacity: ${({ $muted }) => $muted ? 0.84 : 1};
`

const TokenIcon = styled.div<{ $backgroundImage: string, $muted?: boolean }>`
  background-image: url(${props => props.$backgroundImage});
  background-position: center;
  background-repeat: no-repeat;
  background-size: contain;
  flex: 0 0 auto;
  height: 31px;
  opacity: ${({ $muted }) => $muted ? 0.86 : 1};
  width: 31px;
`

const TokenMeta = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`

const TokenTicker = styled.span<{ $muted?: boolean }>`
  color: ${({ $muted }) => $muted ? '#929aad' : '#81899a'};
  font-size: 12px;
  font-weight: 400;
  line-height: 1.25;
`

const TokenBalance = styled.span<{ $muted?: boolean }>`
  color: ${({ $muted }) => $muted ? '#616b7d' : '#465062'};
  font-size: 14.5px;
  font-weight: 500;
  font-variant-numeric: tabular-nums;
  line-height: 1.2;
`

const PulsePanelBody = styled.div`
  border-top: 1px solid #edf1f7;
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 6px;
  justify-content: flex-start;
  min-height: 0;
  padding-top: 5px;
`

const PulseChartBlock = styled.div<{ $hasContent?: boolean }>`
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 4px;
  justify-content: ${({ $hasContent }) => $hasContent ? 'flex-end' : 'center'};
  min-height: 94px;
  min-width: 0;
  transition: opacity 0.18s ease;
`

const PulseChartEmptyState = styled.div`
  color: #7d8492;
  font-size: 12px;
  line-height: 1.4;
  text-align: center;
  width: 100%;
`

const PulseMetricStrip = styled.div`
  border-top: 1px solid #edf1f7;
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  min-height: 52px;
  padding-top: 3px;

  @media (max-width: ${WINDOW_SIZE.TABLET}) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (max-width: ${WINDOW_SIZE.MOBILE_LARGE}) {
    grid-template-columns: 1fr;
  }
`

const PulseMetricStripItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
  padding: 4px 7px 1px;

  &:not(:first-child) {
    border-left: 1px solid #edf1f7;
  }

  @media (max-width: ${WINDOW_SIZE.TABLET}) {
    &:nth-child(2n + 1) {
      border-left: 0;
      padding-left: 0;
    }

    &:nth-child(2n) {
      padding-right: 0;
    }

    &:nth-child(n + 3) {
      border-top: 1px solid #edf1f7;
      padding-top: 5px;
    }
  }

  @media (max-width: ${WINDOW_SIZE.MOBILE_LARGE}) {
    border-left: 0 !important;
    padding-left: 0;
    padding-right: 0;

    &:not(:first-child) {
      border-top: 1px solid #edf1f7;
      padding-top: 5px;
    }
  }
`

const PulseMetricLabel = styled.span`
  color: #6f7888;
  font-size: 10.5px;
  font-weight: 500;
  letter-spacing: 0.03em;
  line-height: 1.25;
  text-transform: uppercase;
`

const PulseMetricValue = styled.span`
  color: #121722;
  font-size: 14.5px;
  font-weight: 500;
  font-variant-numeric: tabular-nums;
  line-height: 1.15;
  overflow-wrap: anywhere;
`

const PulseMetricMeta = styled.span`
  color: #7d8492;
  font-size: 11px;
  line-height: 1.3;
`

const PulseContextCard = styled.div`
  align-items: flex-start;
  display: flex;
  gap: 8px;
  justify-content: space-between;
  min-height: 18px;
  padding-top: 0;
  transition: opacity 0.18s ease;

  @media (max-width: ${WINDOW_SIZE.MOBILE_LARGE}) {
    align-items: flex-start;
    flex-direction: column;
  }
`

const PulseContextEyebrow = styled.div`
  color: #6f7888;
  display: inline;
  font-size: 10.5px;
  font-weight: 500;
  letter-spacing: 0.04em;
  text-transform: uppercase;
`

const PulseContextText = styled.div`
  color: #4f5667;
  font-size: 12px;
  line-height: 1.4;
  margin-top: 0;
`

const BalanceValue = styled.span`
  color: #16191f;
  font-family: var(--font-sans);
  font-size: 38px;
  font-weight: 300;
  font-feature-settings: 'kern' 1, 'pnum' 1, 'tnum' 0;
  font-kerning: normal;
  font-variant-numeric: proportional-nums;
  letter-spacing: -0.005em;
  line-height: 1.04;
  text-shadow: 0 1px 0 rgba(255, 255, 255, 0.55), 0 4px 14px rgba(15, 23, 42, 0.035);
  white-space: nowrap;

  @media (max-width: ${WINDOW_SIZE.MOBILE_LARGE}) {
    font-size: 32px;
    font-weight: 300;
    letter-spacing: -0.005em;
    text-shadow: none;
    white-space: normal;
    line-height: 1.06;
  }
`

const BalanceCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 18px;
  height: 100%;
  justify-content: flex-start;
  width: 100%;
  min-width: 0;

  @media (max-width: ${WINDOW_SIZE.MOBILE_LARGE}) {
    width: 100%;
    min-width: 100%;
  }
`

interface Point {
  x: number
  y: number
}

interface MetricData {
  name: string
  unitLabel: string
  formatLabel: (value: number) => string
  formatYLabelFn?: (value: number) => string
  aggregateLabelValue?: boolean
  data: {
    curPeriod: Point[]
    prevPeriod: Point[]
  }
  dataFn: (numDays: number, testMode: boolean) => Promise<GraphData>
  tooltipMessage?: string
  isLoading: boolean
  flipTrend?: boolean
}

const calculateMetricTrend = (metric: MetricData, flip: boolean = false): React.ReactElement => {
  const pointOne = metric.aggregateLabelValue !== false
    ? metric.data.prevPeriod.reduce((p, n) => p + n.y, 0)
    : metric.data.prevPeriod[metric.data.prevPeriod.length - 1]?.y ?? 0
  const pointTwo = metric.aggregateLabelValue !== false
    ? metric.data.curPeriod.reduce((p, n) => p + n.y, 0)
    : metric.data.curPeriod[metric.data.curPeriod.length - 1]?.y ?? 0

  // Calculate percent change
  const _percentage = ((pointTwo - pointOne) / (pointOne || 1)) * 100
  const percentage = isNaN(_percentage) ? 0 : _percentage

  if (percentage > 0) {
    return <Trend percentage={percentage} isGreen={!flip} trendUp />
  }
  if (percentage < 0) {
    return <Trend percentage={percentage} isGreen={!!flip} trendUp={false} />
  }
  return <TrendNeutral />
}

const getMetricValue = (metric?: MetricData): number => {
  if (!metric) {
    return 0
  }

  return metric.aggregateLabelValue !== false
    ? metric.data.curPeriod.reduce((total, current) => total + current.y, 0)
    : metric.data.curPeriod[metric.data.curPeriod.length - 1]?.y ?? 0
}

const getMetricPreviousValue = (metric?: MetricData): number => {
  if (!metric) {
    return 0
  }

  return metric.aggregateLabelValue !== false
    ? metric.data.prevPeriod.reduce((total, current) => total + current.y, 0)
    : metric.data.prevPeriod[metric.data.prevPeriod.length - 1]?.y ?? 0
}

const formatMetricValue = (metric: MetricData, value: number): string => {
  return metric.formatYLabelFn?.(value) ?? metric.formatLabel(value)
}

const buildCombinedMetric = ({
  name,
  unitLabel,
  metrics,
  formatLabel,
  formatYLabelFn,
  aggregateLabelValue = true,
  tooltipMessage,
  reducer,
  flipTrend
}: {
  name: string
  unitLabel: string
  metrics: Array<MetricData | undefined>
  formatLabel: (value: number) => string
  formatYLabelFn?: (value: number) => string
  aggregateLabelValue?: boolean
  tooltipMessage?: string
  reducer: (values: number[]) => number
  flipTrend?: boolean
}): MetricData => {
  const sourceMetric = metrics.find((metric): metric is MetricData => !!metric)
  const sourceData = sourceMetric?.data ?? EmptyData
  const reducePeriod = (period: 'curPeriod' | 'prevPeriod'): Point[] => {
    return sourceData[period].map((point, idx) => ({
      x: point.x,
      y: reducer(metrics.map(metric => metric?.data[period][idx]?.y ?? 0))
    }))
  }

  return {
    name,
    unitLabel,
    formatLabel,
    formatYLabelFn,
    aggregateLabelValue,
    data: {
      curPeriod: reducePeriod('curPeriod'),
      prevPeriod: reducePeriod('prevPeriod')
    },
    dataFn: async () => [],
    tooltipMessage,
    isLoading: metrics.some(metric => !!metric?.isLoading),
    flipTrend
  }
}

const getEventMetricData = (
  numDays: number,
  events: any[],
  predicate: (event: any) => boolean,
  getValue: (event: any) => number
): GraphData => {
  const days = Math.max(numDays, 2)
  const data = Array(days).fill(0).map((_, idx) => ({
    x: subDays(new Date(), days - idx - 1).getTime(),
    y: 0
  }))

  events.filter(predicate).forEach(event => {
    const eventTime = new Date(event.date).getTime()
    if (eventTime < data[0].x || eventTime > data[data.length - 1].x) {
      return
    }

    const bucket = data.find(point => eventTime <= point.x)
    if (bucket) {
      bucket.y += getValue(event)
    }
  })

  return data
}

const buildMetricFromSeries = ({
  name,
  unitLabel,
  series,
  formatLabel,
  formatYLabelFn,
  aggregateLabelValue = true,
  tooltipMessage,
  flipTrend
}: {
  name: string
  unitLabel: string
  series: GraphData
  formatLabel: (value: number) => string
  formatYLabelFn?: (value: number) => string
  aggregateLabelValue?: boolean
  tooltipMessage?: string
  flipTrend?: boolean
}): MetricData => {
  const midpoint = Math.floor(series.length / 2)

  return {
    name,
    unitLabel,
    formatLabel,
    formatYLabelFn,
    aggregateLabelValue,
    data: {
      curPeriod: series.slice(midpoint),
      prevPeriod: series.slice(0, midpoint)
    },
    dataFn: async () => [],
    tooltipMessage,
    isLoading: false,
    flipTrend
  }
}

interface MetricChartProps {
  metric: MetricData
  height?: number
  formatXAxisLabelFn?: (number: number) => string
  formatXLabelFn?: (number: number) => string
  showYAxis?: boolean
  accentColor?: string
  compact?: boolean
  marginTop?: number
}

const MetricChart = (props: MetricChartProps): React.ReactElement => {
  const { metric } = props
  const accentColor = props.accentColor ?? RADOM_COLORS.RADOM_BLUE
  const Responsive = ResponsiveContainer as any
  const Chart = AreaChart as any
  const chartData = metric.data.curPeriod.map(point => ({
    x: point.x,
    value: point.y
  }))
  const hasVisibleData = chartData.some(point => point.value > 0)

  return <ChartShell $height={props.height ?? 130} $marginTop={props.marginTop}>
    <Responsive width="100%" height="100%">
      <Chart data={chartData} margin={{
        top: props.compact ? 4 : 8,
        right: props.showYAxis ? 12 : props.compact ? 0 : 6,
        bottom: 0,
        left: props.showYAxis ? 4 : 0
      }}>
        <CartesianGrid
          stroke={props.compact ? '#eef2f7' : '#edf1f7'}
          strokeOpacity={props.compact ? 0.6 : 1}
          vertical={false}
        />
        <XAxis
          dataKey="x"
          axisLine={false}
          tickLine={false}
          minTickGap={props.compact ? 38 : 28}
          tick={{ fontSize: props.compact ? 10 : 11, fill: '#8b92a2' }}
          tickFormatter={value => props.formatXAxisLabelFn?.(Number(value)) ?? format(new Date(Number(value)), 'MMM d')}
        />
        <YAxis
          hide={!props.showYAxis}
          axisLine={false}
          tickLine={false}
          width={props.showYAxis ? 72 : 0}
          tick={{ fontSize: 11, fill: '#9aa2b2' }}
          tickFormatter={value => formatMetricValue(metric, Number(value))}
          domain={['auto', 'auto']}
        />
        <RechartsTooltip
          cursor={{ stroke: '#d5dae5', strokeDasharray: '4 4' }}
          contentStyle={{
            border: '1px solid #e3e7ef',
            borderRadius: 8,
            boxShadow: '0 12px 30px rgba(15, 23, 42, 0.12)',
            color: '#111827',
            fontSize: 13
          }}
          labelFormatter={value => props.formatXLabelFn?.(Number(value)) ?? format(new Date(Number(value)), 'MMM d, yyyy')}
          formatter={(value) => {
            const numericValue = Array.isArray(value) ? Number(value[0]) : Number(value)
            return [
              formatMetricValue(metric, numericValue),
              'Current period'
            ]
          }}
        />
        <Area
          dataKey="value"
          type="monotone"
          stroke={accentColor}
          strokeOpacity={props.compact && !hasVisibleData ? 0.42 : 1}
          strokeWidth={props.compact ? 1.8 : 2}
          fill={accentColor}
          fillOpacity={props.compact ? hasVisibleData ? 0.05 : 0.02 : 0.08}
          dot={false}
          activeDot={{ r: 4, strokeWidth: 2, stroke: '#fff', fill: accentColor }}
        />
      </Chart>
    </Responsive>
    {metric.isLoading && <ChartLoading>
      <LoadingBar style={{ height: 12, width: 110 }} />
    </ChartLoading>}
  </ChartShell>
}

interface MetricCardViewProps {
  metric: MetricData
  chartHeight?: number
  large?: boolean
  formatXAxisLabelFn?: (number: number) => string
  formatXLabelFn?: (number: number) => string
  showYAxis?: boolean
  accentColor?: string
  onExpand?: (metric: MetricData) => void
}

const MetricCardView = (props: MetricCardViewProps): React.ReactElement => {
  const { metric } = props
  return <>
    <MetricHeader>
      <MetricName>
        <span>{metric.name}</span>
        <MetricUnit>({metric.unitLabel})</MetricUnit>
        {metric.tooltipMessage && <Tooltip message={metric.tooltipMessage} />}
      </MetricName>
      {props.onExpand && <ChartActionButton
        aria-label={`View ${metric.name} chart larger`}
        title='View larger'
        type='button'
        onClick={() => props.onExpand?.(metric)}
      >
        <ExpandIcon style={{ height: 16, width: 16 }} />
      </ChartActionButton>}
    </MetricHeader>
    <MetricValueRow>
      <MetricValue $large={props.large}>
        {metric.formatLabel(getMetricValue(metric))}
      </MetricValue>
      {metric.isLoading
        ? <LoadingBar style={{ height: 15, width: 70 }} />
        : calculateMetricTrend(metric, metric.flipTrend)}
    </MetricValueRow>
    <MetricPrevious>
      Previous period {metric.formatLabel(getMetricPreviousValue(metric))}
    </MetricPrevious>
    <MetricChart
      metric={metric}
      height={props.chartHeight}
      formatXAxisLabelFn={props.formatXAxisLabelFn}
      formatXLabelFn={props.formatXLabelFn}
      showYAxis={props.showYAxis}
      accentColor={props.accentColor}
    />
  </>
}

interface ExpandedMetric {
  metric: MetricData
  accentColor: string
  formatXAxisLabelFn?: (number: number) => string
  formatXLabelFn?: (number: number) => string
  isPrimary?: boolean
}

interface HomeProps {
  mode?: HomeMode
}

const Home = observer(({ mode = 'home' }: HomeProps) => {
  const navigate = useNavigate()
  const location = useLocation()

  const [testMode] = useTestMode()
  const [selectedTimeRangeValue, setSelectedTimeRangeValue] = useState(TIME_RANGE_OPTIONS[0].numDays)
  const [selectedTimeRange, setSelectedTimeRange] = useState(TIME_RANGE_OPTIONS[0].title)
  const [mainChartMode, setMainChartMode] = useState<MainChartMode>('revenue')
  const searchParams = new URLSearchParams(location.search)
  const requestedTab = searchParams.get('tab')
  const initialTab = DASHBOARD_TABS.some(tab => tab.value === requestedTab)
    ? requestedTab as DashboardTab
    : 'overview'
  const [activeTab, setActiveTab] = useState<DashboardTab>(initialTab)
  const [expandedMetric, setExpandedMetric] = useState<ExpandedMetric | undefined>()
  const [sellerProfile, setSellerProfile] = useState<IOrganization | undefined>(Radom.sellerProfile ?? undefined)
  const [isSellerProfileLoading, setIsSellerProfileLoading] = useState(!Radom.sellerProfile)
  const [topCustomers, setTopCustomers] = useState<Customer[]>([])
  const [totalCustomerCount, setTotalCustomerCount] = useState(0)
  const [isCustomersLoading, setIsCustomersLoading] = useState(false)
  const [recentProducts, setRecentProducts] = useState<IProduct[]>([])
  const [isProductsLoading, setIsProductsLoading] = useState(false)
  const [productRevenueById, setProductRevenueById] = useState<Record<string, number>>({})
  const [isProductRevenueLoading, setIsProductRevenueLoading] = useState(false)
  const [virtualAccounts, setVirtualAccounts] = useState<DepositAccount[]>([])
  const [isVirtualAccountsLoading, setIsVirtualAccountsLoading] = useState(false)
  const [virtualAccountsCapabilities, setVirtualAccountsCapabilities] = useState<BridgeVirtualAccountCapabilities>({
    canOpenAch: false,
    canOpenSepa: false,
    canOpenSpei: false,
    canOpenPix: false
  })
  const formatCurrencyNew = (v: number): string => formatCurrency(v, User.organization?.defaultCurrency ?? 'USD')
  const formatHomeBalanceValue = (v: number): string => {
    const currency = User.organization?.defaultCurrency ?? 'USD'

    if (['USDT', 'USDC', 'EURC', 'ETH'].includes(currency)) {
      return `${new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(v)} ${currency}`
    }

    try {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(v)
    } catch {
      return new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(v)
    }
  }
  const isQaSeed = searchParams.get('qa') === '1'
  const getAnalyticsUrl = (tab?: DashboardTab): string => {
    const params = new URLSearchParams()

    if (tab !== undefined && tab !== 'overview') {
      params.set('tab', tab)
    }

    if (isQaSeed) {
      params.set('qa', '1')
    }

    const query = params.toString()
    return `/analytics${query ? `?${query}` : ''}`
  }

  const handleAnalyticsTabChange = (tab: DashboardTab): void => {
    setActiveTab(tab)
    navigate(getAnalyticsUrl(tab), { replace: false })
  }

  const [mainMetric, setMainMetric] = useState<MetricData>({
    name: 'Gross revenue',
    unitLabel: '$',
    formatLabel: (v: number) => formatCurrencyNew(v),
    data: EmptyData,
    dataFn: Analytics.getGrossVolume,
    tooltipMessage: 'Estimated gross revenue over the selected period.',
    formatYLabelFn: (v: number) => formatCurrencyNew(v),
    aggregateLabelValue: true,
    isLoading: true
  })
  const [metrics, setMetrics] = useState<MetricData[]>([
    {
      name: 'Gross volume',
      unitLabel: '$',
      formatLabel: (v: number) => formatCurrencyNew(v),
      data: EmptyData,
      dataFn: Analytics.getGrossVolume,
      tooltipMessage: 'Estimated sum of all your revenue.',
      formatYLabelFn: (v: number) => formatCurrencyNew(v),
      aggregateLabelValue: true,
      isLoading: true
    },
    {
      name: 'Net volume',
      unitLabel: '$',
      formatLabel: (v: number) => formatCurrencyNew(v),
      data: EmptyData,
      dataFn: Analytics.getNetVolume,
      tooltipMessage: "Estimated sum of all your revenue minus Radom's protocol fees.",
      formatYLabelFn: (v: number) => formatCurrencyNew(v),
      aggregateLabelValue: true,
      isLoading: true
    },
    {
      name: 'Recurring payments',
      unitLabel: '#',
      formatLabel: (v: number) => v.toString(),
      data: EmptyData,
      dataFn: Analytics.getRecurringPayments,
      aggregateLabelValue: true,
      isLoading: true
    },
    {
      name: 'One-time payments',
      unitLabel: '#',
      formatLabel: (v: number) => v.toString(),
      data: EmptyData,
      dataFn: Analytics.getOneTimePayments,
      aggregateLabelValue: true,
      isLoading: true
    },
    {
      name: 'Average payment size',
      unitLabel: '$',
      formatLabel: (v: number) => formatCurrencyNew(v),
      data: EmptyData,
      dataFn: Analytics.getAveragePaymentSize,
      tooltipMessage: 'Average completed payment amount per day.',
      formatYLabelFn: (v: number) => formatCurrencyNew(v),
      aggregateLabelValue: false,
      isLoading: true
    },
    {
      name: 'Total subscribers',
      unitLabel: '#',
      formatLabel: (v: number) => v.toString(),
      data: EmptyData,
      dataFn: Analytics.getTotalSubscribers,
      aggregateLabelValue: false,
      isLoading: true
    },
    {
      name: 'New subscriptions',
      unitLabel: '#',
      formatLabel: (v: number) => v.toString(),
      data: EmptyData,
      dataFn: Analytics.getNewSubscriptions,
      aggregateLabelValue: true,
      isLoading: true
    },
    {
      name: 'Subscription cancellations',
      unitLabel: '#',
      formatLabel: (v: number) => v.toString(),
      data: EmptyData,
      dataFn: Analytics.getCancelledSubscriptions,
      aggregateLabelValue: true,
      isLoading: true,
      flipTrend: true
    },
    {
      name: 'Subscription expirations',
      unitLabel: '#',
      formatLabel: (v: number) => v.toString(),
      data: EmptyData,
      dataFn: Analytics.getExpiredSubscriptions,
      aggregateLabelValue: true,
      isLoading: true,
      flipTrend: true
    },
    {
      name: 'Subscription churn',
      unitLabel: '%',
      formatLabel: (v: number) => `${v.toFixed(1)}%`,
      formatYLabelFn: (v: number) => `${v.toFixed(1)}%`,
      data: EmptyData,
      dataFn: Analytics.getSubscriptionChurnRate,
      tooltipMessage: 'Cancelled and expired subscriptions divided by the subscriber base for each day.',
      aggregateLabelValue: false,
      isLoading: true,
      flipTrend: true
    },
    {
      name: 'Crypto checkout conversion',
      unitLabel: '%',
      formatLabel: (v: number) => `${v.toFixed(1)}%`,
      formatYLabelFn: (v: number) => `${v.toFixed(1)}%`,
      data: EmptyData,
      dataFn: Analytics.getCryptoCheckoutConversionRate,
      tooltipMessage: 'Completed crypto checkout sessions divided by started sessions.',
      aggregateLabelValue: false,
      isLoading: true
    },
    {
      name: 'Open banking conversion',
      unitLabel: '%',
      formatLabel: (v: number) => `${v.toFixed(1)}%`,
      formatYLabelFn: (v: number) => `${v.toFixed(1)}%`,
      data: EmptyData,
      dataFn: Analytics.getOpenBankingCheckoutConversionRate,
      tooltipMessage: 'Completed open banking checkout sessions divided by started sessions.',
      aggregateLabelValue: false,
      isLoading: true
    },
    {
      name: 'Crypto checkout starts',
      unitLabel: '#',
      formatLabel: (v: number) => Math.round(v).toLocaleString(),
      data: EmptyData,
      dataFn: Analytics.getCryptoCheckoutStarted,
      tooltipMessage: 'Started crypto checkout sessions.',
      aggregateLabelValue: true,
      isLoading: true
    },
    {
      name: 'Open banking checkout starts',
      unitLabel: '#',
      formatLabel: (v: number) => Math.round(v).toLocaleString(),
      data: EmptyData,
      dataFn: Analytics.getOpenBankingCheckoutStarted,
      tooltipMessage: 'Started open banking checkout sessions.',
      aggregateLabelValue: true,
      isLoading: true
    },
    {
      name: 'Crypto completed checkouts',
      unitLabel: '#',
      formatLabel: (v: number) => Math.round(v).toLocaleString(),
      data: EmptyData,
      dataFn: Analytics.getCryptoCheckoutCompleted,
      tooltipMessage: 'Completed crypto checkout sessions.',
      aggregateLabelValue: true,
      isLoading: true
    },
    {
      name: 'Open banking completed checkouts',
      unitLabel: '#',
      formatLabel: (v: number) => Math.round(v).toLocaleString(),
      data: EmptyData,
      dataFn: Analytics.getOpenBankingCheckoutCompleted,
      tooltipMessage: 'Completed open banking checkout sessions.',
      aggregateLabelValue: true,
      isLoading: true
    }
  ])

  const loadMetrics = async (numDays: number): Promise<void> => {
    if (isQaSeed) {
      const applyQaMetric = (metric: MetricData): MetricData => {
        const qaData = getQaMetricData(metric.name, numDays)
        const midpoint = Math.floor(qaData.length / 2)
        return {
          ...metric,
          data: {
            curPeriod: qaData.slice(midpoint),
            prevPeriod: qaData.slice(0, midpoint)
          },
          isLoading: false
        }
      }

      setMetrics(metrics.map(applyQaMetric))
      setMainMetric(applyQaMetric(mainMetric))
      return
    }

    try {
      await User.getOrganization()
      await Treasury.loadPriceQuotes()
    } catch {}

    const currentMetrics = metrics
    const currentMainMetric = mainMetric
    const emptyData = getEmptyDataForRange(numDays)
    setMetrics(currentMetrics.map(m => ({
      ...m,
      data: m.data.curPeriod.length > 0 || m.data.prevPeriod.length > 0 ? m.data : emptyData,
      isLoading: true
    })))
    setMainMetric({
      ...currentMainMetric,
      data: currentMainMetric.data.curPeriod.length > 0 || currentMainMetric.data.prevPeriod.length > 0
        ? currentMainMetric.data
        : emptyData,
      isLoading: true
    })

    const metricResults = await Promise.allSettled(
      currentMetrics.map(async m => await m.dataFn(numDays, testMode))
    )

    setMetrics(currentMetrics.map((metric, idx) => {
      const result = metricResults[idx]
      if (result.status === 'rejected') {
        console.error(`Failed to load metric ${metric.name}`, result.reason)
        return {
          ...metric,
          data: emptyData,
          isLoading: false
        }
      }

      const midpoint = Math.floor(result.value.length / 2)
      return {
        ...metric,
        data: {
          curPeriod: result.value.slice(midpoint),
          prevPeriod: result.value.slice(0, midpoint)
        },
        isLoading: false
      }
    }))

    try {
      const mainData = await currentMainMetric.dataFn(numDays, testMode)
      const midpoint = Math.floor(mainData.length / 2)
      setMainMetric({
        ...currentMainMetric,
        isLoading: false,
        data: {
          curPeriod: mainData.slice(midpoint),
          prevPeriod: mainData.slice(0, midpoint)
        }
      })
    } catch (err: any) {
      console.error('Failed to load main dashboard metric', err)
      errorToast(err.reason || err.message)
      setMainMetric({
        ...currentMainMetric,
        data: emptyData,
        isLoading: false
      })
    }
  }

  useEffect(() => {
    if (!User.organization) {
      return
    }

    if (isQaSeed) {
      loadMetrics(selectedTimeRangeValue)
      return
    }

    Treasury.loadBalances()
    Events.loadEvents(testMode, selectedTimeRangeValue).finally(async () => await loadMetrics(selectedTimeRangeValue))
  }, [testMode, selectedTimeRangeValue, User.organization, isQaSeed])

  useEffect(() => {
    if (!User.organization) {
      return
    }

    if (isQaSeed) {
      const qaSellerProfile: IOrganization = {
        organizationId: User.selectedOrganizationId ?? '',
        name: 'Radom QA Studio',
        logoUrl: 'https://uploads-ssl.webflow.com/635182914b66e31f085ba568/635182914b66e3594c5ba575_Radom%20Full%20Logo.svg',
        website: 'https://radom.com',
        defaultCurrency: User.organization?.defaultCurrency ?? 'USD',
        verificationStatus: 'Unverified'
      }

      setSellerProfile(qaSellerProfile)
      setTopCustomers(QA_TOP_CUSTOMERS)
      setTotalCustomerCount(42)
      setRecentProducts(QA_RECENT_PRODUCTS)
      setProductRevenueById(QA_PRODUCT_REVENUE_BY_ID)
      setVirtualAccounts(QA_VIRTUAL_ACCOUNTS)
      setVirtualAccountsCapabilities({
        canOpenAch: true,
        canOpenSepa: true,
        canOpenSpei: false,
        canOpenPix: false
      })
      return
    }

    setIsSellerProfileLoading(true)
    Radom.getSellerProfile()
      .then(profile => setSellerProfile(profile))
      .catch(err => {
        console.error('Failed to load seller profile summary', err)
        setSellerProfile(undefined)
      })
      .finally(() => setIsSellerProfileLoading(false))

    setIsCustomersLoading(true)
    Radom.listCustomers(0, 5, testMode ? testNetworks : prodNetworks, 'totalSpendDescending')
      .then(res => {
        setTopCustomers(res.data)
        setTotalCustomerCount(res.total)
      })
      .catch(err => {
        console.error('Failed to load customer summary', err)
        setTopCustomers([])
        setTotalCustomerCount(0)
      })
      .finally(() => setIsCustomersLoading(false))

    setIsProductsLoading(true)
    Radom.getProducts(false, 1000, 0, 'updated_at', 'desc')
      .then(res => setRecentProducts(res.data.filter(product => !product.isArchived)))
      .catch(err => {
        console.error('Failed to load product summary', err)
        setRecentProducts([])
      })
      .finally(() => setIsProductsLoading(false))

    setIsVirtualAccountsLoading(true)
    Promise.allSettled([
      Radom.getVirtualAccountsCapabilities(),
      Radom.listDepositAccounts(testMode, 0, 4, undefined, [DepositAccountType.Fiat])
    ])
      .then(([capabilitiesResult, accountsResult]) => {
        if (capabilitiesResult.status === 'fulfilled') {
          setVirtualAccountsCapabilities(capabilitiesResult.value)
        }
        if (accountsResult.status === 'fulfilled') {
          setVirtualAccounts(accountsResult.value.data)
        }
      })
      .catch(err => {
        console.error('Failed to load virtual account summary', err)
        setVirtualAccounts([])
      })
      .finally(() => setIsVirtualAccountsLoading(false))
  }, [User.organization, isQaSeed, testMode])

  useEffect(() => {
    if (!User.organization || recentProducts.length <= 0) {
      setProductRevenueById({})
      return
    }

    if (isQaSeed) {
      setProductRevenueById(QA_PRODUCT_REVENUE_BY_ID)
      setIsProductRevenueLoading(false)
      return
    }

    let isCancelled = false

    const loadTrackedProductRevenue = async (): Promise<void> => {
      setIsProductRevenueLoading(true)

      try {
        await Treasury.loadPriceQuotes()

        const defaultCurrency = User.organization?.defaultCurrency ?? 'USD'
        const trackedProductIds = new Set(recentProducts.map(product => product.id))
        const currentPeriodStart = subDays(new Date(), Math.max(1, Math.floor(selectedTimeRangeValue / 2)))

        const isInCurrentPeriod = (value?: string | Date): boolean => {
          if (!value) {
            return false
          }

          const parsed = value instanceof Date ? value : new Date(value)
          return !Number.isNaN(parsed.getTime()) && parsed >= currentPeriodStart
        }

        const convertToDefaultCurrency = (amount: number, currency?: string): number => {
          if (!Number.isFinite(amount) || amount <= 0) {
            return 0
          }

          if (!currency || currency.toLowerCase() === defaultCurrency.toLowerCase()) {
            return amount
          }

          const quote = Treasury.priceQuotes.find(priceQuote =>
            priceQuote.from.toLowerCase() === currency.toLowerCase() &&
            priceQuote.to.toLowerCase() === defaultCurrency.toLowerCase()
          )

          return quote ? amount * quote.value : amount
        }

        const trackedRevenue: Record<string, number> = {}

        const addTrackedRevenue = (productId: string, amount: number, currency?: string): void => {
          if (!trackedProductIds.has(productId)) {
            return
          }

          const convertedAmount = convertToDefaultCurrency(amount, currency)
          if (convertedAmount <= 0) {
            return
          }

          trackedRevenue[productId] = (trackedRevenue[productId] ?? 0) + convertedAmount
        }

        const [
          sellerInvoicesResult,
          checkoutSessionsResult,
          paymentLinksResult,
          subscriptionsResult
        ] = await Promise.allSettled([
          Radom.getSellerInvoices(),
          Radom.listCheckoutSessions(0, 80),
          Radom.listPaymentLinks([], 80, 0, 'updated_at', 'DESC'),
          Radom.listSubscriptions(
            new Date().toISOString(),
            currentPeriodStart.toISOString(),
            currentPeriodStart.toISOString(),
            testMode ? testNetworks : prodNetworks,
            ['active', 'cancelled', 'expired'],
            0,
            80
          )
        ])

        if (sellerInvoicesResult.status === 'fulfilled') {
          const relevantInvoices = sellerInvoicesResult.value
            .filter(invoice => invoice.status === 'paid' && isInCurrentPeriod(invoice.paidAt || invoice.issuedAt))
            .slice(0, 30)

          const invoiceDetails = await Promise.allSettled(
            relevantInvoices.map(async invoice => await Radom.getInvoice(invoice.id))
          )

          invoiceDetails.forEach(result => {
            if (result.status !== 'fulfilled') {
              return
            }

            result.value.products.forEach(({ product, quantity }) => {
              addTrackedRevenue(
                product.id,
                Number(product.price || 0) * Number(quantity || 1),
                product.currency
              )
            })
          })
        }

        if (checkoutSessionsResult.status === 'fulfilled') {
          checkoutSessionsResult.value.data
            .filter(session =>
              session.sessionStatus === 'success' &&
              isInCurrentPeriod(session.updatedAt || session.createdAt) &&
              session.products?.some(product => trackedProductIds.has(product.id))
            )
            .forEach(session => {
              const linkedProducts = (session.products || []).filter(product => trackedProductIds.has(product.id))
              const linkedSubtotal = linkedProducts.reduce((total, product) => total + Number(product.price || 0), 0)
              const realizedTotal = Number(session.total || linkedSubtotal)

              linkedProducts.forEach(product => {
                const allocatedAmount = linkedSubtotal > 0
                  ? realizedTotal * (Number(product.price || 0) / linkedSubtotal)
                  : Number(product.price || 0)

                addTrackedRevenue(product.id, allocatedAmount, product.currency || session.currency)
              })
            })
        }

        if (paymentLinksResult.status === 'fulfilled') {
          const relevantLinks = paymentLinksResult.value.data
            .filter(link => !link.isArchived && link.products.some(product => trackedProductIds.has(product.id)))
            .slice(0, 20)

          const linkPayments = await Promise.allSettled(
            relevantLinks.map(async link => ({
              link,
              payments: await Radom.listPaymentsByPaymentLinkId(link.id, 100, 0)
            }))
          )

          linkPayments.forEach(result => {
            if (result.status !== 'fulfilled') {
              return
            }

            const linkedProducts = result.value.link.products.filter(product => trackedProductIds.has(product.id))
            const linkedSubtotal = linkedProducts.reduce((total, product) => total + Number(product.price || 0), 0)

            result.value.payments.data
              .filter(payment => isInCurrentPeriod(payment.completedAt))
              .forEach(payment => {
                const realizedTotal = Number(payment.subtotal ?? payment.total ?? linkedSubtotal)

                linkedProducts.forEach(product => {
                  const allocatedAmount = linkedSubtotal > 0
                    ? realizedTotal * (Number(product.price || 0) / linkedSubtotal)
                    : Number(product.price || 0)

                  addTrackedRevenue(product.id, allocatedAmount, product.currency || payment.currency)
                })
              })
          })
        }

        if (subscriptionsResult.status === 'fulfilled') {
          subscriptionsResult.value.data.forEach(subscription => {
            const productId = subscription.product?.id
            if (!productId || !trackedProductIds.has(productId) || !subscription.payments) {
              return
            }

            subscription.payments
              .filter(payment => isInCurrentPeriod(payment.date))
              .forEach(() => {
                addTrackedRevenue(productId, Number(subscription.amount || 0), subscription.currency)
              })
          })
        }

        if (!isCancelled) {
          setProductRevenueById(trackedRevenue)
        }
      } catch (err) {
        console.error('Failed to load tracked product revenue', err)
        if (!isCancelled) {
          setProductRevenueById({})
        }
      } finally {
        if (!isCancelled) {
          setIsProductRevenueLoading(false)
        }
      }
    }

    loadTrackedProductRevenue()

    return () => {
      isCancelled = true
    }
  }, [User.organization, isQaSeed, recentProducts, selectedTimeRangeValue, testMode])

  useEffect(() => {
    if (mode !== 'analytics') {
      return
    }

    if (DASHBOARD_TABS.some(tab => tab.value === requestedTab)) {
      setActiveTab(requestedTab as DashboardTab)
    }
  }, [mode, requestedTab])

  const grossVolumeMetric = metrics.find(m => m.name === 'Gross volume')
  const netVolumeMetric = metrics.find(m => m.name === 'Net volume')
  const recurringPaymentsMetric = metrics.find(m => m.name === 'Recurring payments')
  const oneTimePaymentsMetric = metrics.find(m => m.name === 'One-time payments')
  const cryptoConversionMetric = metrics.find(m => m.name === 'Crypto checkout conversion')
  const openBankingConversionMetric = metrics.find(m => m.name === 'Open banking conversion')
  const cryptoCheckoutStartsMetric = metrics.find(m => m.name === 'Crypto checkout starts')
  const openBankingCheckoutStartsMetric = metrics.find(m => m.name === 'Open banking checkout starts')
  const cryptoCheckoutCompletedMetric = metrics.find(m => m.name === 'Crypto completed checkouts')
  const openBankingCheckoutCompletedMetric = metrics.find(m => m.name === 'Open banking completed checkouts')
  const paymentCountMetric = buildCombinedMetric({
    name: 'Completed payments',
    unitLabel: '#',
    metrics: [oneTimePaymentsMetric, recurringPaymentsMetric],
    formatLabel: (v: number) => Math.round(v).toLocaleString(),
    aggregateLabelValue: true,
    tooltipMessage: 'Completed one-time and recurring payments for the selected period.',
    reducer: values => values.reduce((total, value) => total + value, 0)
  })
  const conversionRateMetric = buildCombinedMetric({
    name: 'Checkout conversion',
    unitLabel: '%',
    metrics: [cryptoConversionMetric, openBankingConversionMetric],
    formatLabel: (v: number) => `${v.toFixed(1)}%`,
    formatYLabelFn: (v: number) => `${v.toFixed(1)}%`,
    aggregateLabelValue: false,
    tooltipMessage: 'Average checkout completion across crypto and open banking sessions.',
    reducer: values => {
      const validValues = values.filter(value => value > 0)
      return validValues.length > 0
        ? validValues.reduce((total, value) => total + value, 0) / validValues.length
        : 0
    }
  })
  const checkoutStartsMetric = buildCombinedMetric({
    name: 'Checkout starts',
    unitLabel: '#',
    metrics: [cryptoCheckoutStartsMetric, openBankingCheckoutStartsMetric],
    formatLabel: (v: number) => Math.round(v).toLocaleString(),
    aggregateLabelValue: true,
    tooltipMessage: 'Started crypto and open banking checkout sessions.',
    reducer: values => values.reduce((total, value) => total + value, 0)
  })
  const checkoutCompletedMetric = buildCombinedMetric({
    name: 'Completed checkouts',
    unitLabel: '#',
    metrics: [cryptoCheckoutCompletedMetric, openBankingCheckoutCompletedMetric],
    formatLabel: (v: number) => Math.round(v).toLocaleString(),
    aggregateLabelValue: true,
    tooltipMessage: 'Completed crypto and open banking checkout sessions.',
    reducer: values => values.reduce((total, value) => total + value, 0)
  })
  const primaryChartMetric = mainChartMode === 'payments'
    ? paymentCountMetric
    : mainChartMode === 'conversion'
      ? conversionRateMetric
      : {
        ...(grossVolumeMetric ?? mainMetric),
        name: 'Gross revenue',
        tooltipMessage: 'Estimated gross revenue over the selected period.'
      }
  const totalPaymentsInRange = getMetricValue(recurringPaymentsMetric) + getMetricValue(oneTimePaymentsMetric)
  const averagePaymentSizeLabel = totalPaymentsInRange > 0 && grossVolumeMetric
    ? formatCurrencyNew(getMetricValue(grossVolumeMetric) / totalPaymentsInRange)
    : formatCurrencyNew(0)
  const averageCheckoutConversion = [cryptoConversionMetric, openBankingConversionMetric]
    .map(metric => getMetricValue(metric))
    .filter(value => value > 0)
  const checkoutConversionLabel = averageCheckoutConversion.length > 0
    ? `${(averageCheckoutConversion.reduce((total, value) => total + value, 0) / averageCheckoutConversion.length).toFixed(1)}%`
    : '0.0%'
  const revenueMetricNames = ['Net volume', 'One-time payments', 'Recurring payments', 'Average payment size']
  const subscriptionMetricNames = [
    'Total subscribers',
    'New subscriptions',
    'Subscription cancellations',
    'Subscription churn'
  ]
  const revenueMetrics = metrics.filter(m => revenueMetricNames.includes(m.name))
  const checkoutMetrics = [
    cryptoConversionMetric,
    openBankingConversionMetric,
    checkoutStartsMetric,
    checkoutCompletedMetric
  ].filter((metric): metric is MetricData => !!metric)
  const subscriptionMetrics = metrics.filter(m => subscriptionMetricNames.includes(m.name))
  const buildDashboardMetric = ({
    name,
    unitLabel,
    formatLabel,
    formatYLabelFn,
    aggregateLabelValue,
    tooltipMessage,
    predicate,
    getValue,
    flipTrend
  }: {
    name: string
    unitLabel: string
    formatLabel: (value: number) => string
    formatYLabelFn?: (value: number) => string
    aggregateLabelValue?: boolean
    tooltipMessage?: string
    predicate: (event: any) => boolean
    getValue: (event: any) => number
    flipTrend?: boolean
  }): MetricData => buildMetricFromSeries({
    name,
    unitLabel,
    series: isQaSeed
      ? getQaMetricData(name, selectedTimeRangeValue)
      : getEventMetricData(selectedTimeRangeValue, Events.managedEvents, predicate, getValue),
    formatLabel,
    formatYLabelFn,
    aggregateLabelValue,
    tooltipMessage,
    flipTrend
  })
  const paymentEventNames = ['PaymentEvent', 'RecurringPaymentEvent', 'SubscriptionPaymentEvent', 'OpenBanking']
  const isCompletedPaymentEvent = (event: any): boolean => paymentEventNames.includes(event.name)
  const getPaymentAmount = (event: any): number => {
    const summaryAmount = event.paymentSummary?.grossAmount
    return Number(summaryAmount ?? event.amount ?? 0)
  }
  const productMetrics = [
    buildDashboardMetric({
      name: 'Invoice revenue',
      unitLabel: '$',
      formatLabel: (v: number) => formatCurrencyNew(v),
      formatYLabelFn: (v: number) => formatCurrencyNew(v),
      tooltipMessage: 'Completed revenue where the source is a hosted invoice.',
      predicate: event => isCompletedPaymentEvent(event) &&
        event.associatedEntity?.entityType === AssociatedEntityType.Invoice,
      getValue: getPaymentAmount
    }),
    buildDashboardMetric({
      name: 'Payment link revenue',
      unitLabel: '$',
      formatLabel: (v: number) => formatCurrencyNew(v),
      formatYLabelFn: (v: number) => formatCurrencyNew(v),
      tooltipMessage: 'Completed revenue from payment links and donation links.',
      predicate: event => isCompletedPaymentEvent(event) &&
        [
          AssociatedEntityType.PaymentLink,
          AssociatedEntityType.DonationLink
        ].includes(event.associatedEntity?.entityType),
      getValue: getPaymentAmount
    }),
    buildDashboardMetric({
      name: 'Checkout session revenue',
      unitLabel: '$',
      formatLabel: (v: number) => formatCurrencyNew(v),
      formatYLabelFn: (v: number) => formatCurrencyNew(v),
      tooltipMessage: 'Completed revenue from checkout sessions.',
      predicate: event => isCompletedPaymentEvent(event) &&
        event.associatedEntity?.entityType === AssociatedEntityType.CheckoutSession,
      getValue: getPaymentAmount
    }),
    buildDashboardMetric({
      name: 'Payment session revenue',
      unitLabel: '$',
      formatLabel: (v: number) => formatCurrencyNew(v),
      formatYLabelFn: (v: number) => formatCurrencyNew(v),
      tooltipMessage: 'Completed revenue from direct payment sessions.',
      predicate: event => isCompletedPaymentEvent(event) &&
        event.associatedEntity?.entityType === AssociatedEntityType.PaymentSession,
      getValue: getPaymentAmount
    }),
    buildDashboardMetric({
      name: 'Deposit address volume',
      unitLabel: '$',
      formatLabel: (v: number) => formatCurrencyNew(v),
      formatYLabelFn: (v: number) => formatCurrencyNew(v),
      tooltipMessage: 'Deposit volume received through persistent deposit addresses.',
      predicate: event => event.name === 'Deposit',
      getValue: event => Number(event.data?.fiatGrossAmount ?? event.data?.grossAmount ?? 0)
    })
  ]
  const bankingMetrics = [
    buildDashboardMetric({
      name: 'Open banking payments',
      unitLabel: '#',
      formatLabel: (v: number) => Math.round(v).toLocaleString(),
      tooltipMessage: 'Completed open banking payments.',
      predicate: event => event.name === 'OpenBanking',
      getValue: () => 1
    }),
    buildDashboardMetric({
      name: 'Open banking volume',
      unitLabel: '$',
      formatLabel: (v: number) => formatCurrencyNew(v),
      formatYLabelFn: (v: number) => formatCurrencyNew(v),
      tooltipMessage: 'Estimated gross open banking volume in the organization default currency when available.',
      predicate: event => event.name === 'OpenBanking',
      getValue: getPaymentAmount
    }),
    buildDashboardMetric({
      name: 'Virtual account deposits',
      unitLabel: '$',
      formatLabel: (v: number) => formatCurrencyNew(v),
      formatYLabelFn: (v: number) => formatCurrencyNew(v),
      tooltipMessage: 'Fiat deposits received through virtual accounts.',
      predicate: event => event.name === 'Deposit',
      getValue: event => Number(event.data?.fiatGrossAmount ?? event.data?.grossAmount ?? 0)
    }),
    buildDashboardMetric({
      name: 'Off-ramp volume',
      unitLabel: '$',
      formatLabel: (v: number) => formatCurrencyNew(v),
      formatYLabelFn: (v: number) => formatCurrencyNew(v),
      tooltipMessage: 'Successful payout and withdrawal volume from Radom balances.',
      predicate: event => event.name === 'Withdrawal' && event.data?.isSuccess,
      getValue: event => Number(event.data?.withdrawalAmount ?? 0)
    })
  ]
  const qaAssetRowsByToken = new Map(QA_ASSET_ROWS.map(asset => [asset.token, asset]))
  const balanceAssetRows = isQaSeed
    ? HOME_BALANCE_TOKENS.map(token => ({
      token,
      balance: qaAssetRowsByToken.get(token)?.balance ?? 0
    }))
    : uniq(
      Treasury.managedBalances
        .map(b => getMethod(b.network, b.token).ticker)
        .filter(t => Treasury.assetSum(t, testMode) > 0)
        .concat(HOME_BALANCE_TOKENS)
    )
      .map(token => ({
        token,
        balance: Treasury.loading ? undefined : Number(Treasury.assetSum(token, testMode).toFixed(8)),
        fiatValue: Treasury.loading ? 0 : Treasury.dollarAssetSum(token, testMode),
        defaultIndex: HOME_BALANCE_TOKENS.indexOf(token)
      }))
      .sort((a, b) => {
        if (b.fiatValue !== a.fiatValue) {
          return b.fiatValue - a.fiatValue
        }

        if (a.defaultIndex !== -1 || b.defaultIndex !== -1) {
          return (a.defaultIndex === -1 ? Number.MAX_SAFE_INTEGER : a.defaultIndex) -
            (b.defaultIndex === -1 ? Number.MAX_SAFE_INTEGER : b.defaultIndex)
        }

        return a.token.localeCompare(b.token)
      })
      .slice(0, HOME_BALANCE_PRIMARY_LIMIT)
  const primaryBalanceAssets = balanceAssetRows.slice(0, HOME_BALANCE_PRIMARY_LIMIT)
  const balanceValue = isQaSeed
    ? 248104.32
    : Treasury.totalEstimatedDollarBalance(testMode)
  const hasMetricData = (metric: MetricData): boolean =>
    getMetricValue(metric) > 0 || getMetricPreviousValue(metric) > 0
  const visibleMetrics = (metricList: MetricData[]): MetricData[] =>
    isQaSeed || Events.isLoading || metricList.some(metric => metric.isLoading)
      ? metricList
      : metricList.filter(hasMetricData)
  const visibleProductMetrics = visibleMetrics(productMetrics)
  const visibleBankingMetrics = visibleMetrics(bankingMetrics)
  const visibleSubscriptionMetrics = visibleMetrics(subscriptionMetrics)
  const hasProductTabData = visibleProductMetrics.length > 0 || visibleSubscriptionMetrics.length > 0
  const hasBankingTabData = visibleBankingMetrics.length > 0
  const sellerProfileIsComplete = !!sellerProfile?.name && !!sellerProfile?.logoUrl && !!sellerProfile?.website
  const hasResolvedSellerProfile = sellerProfileIsComplete || !isSellerProfileLoading
  const shouldPromptSellerProfile = hasResolvedSellerProfile && !sellerProfileIsComplete
  const sellerProfileStatus = isSellerProfileLoading
    ? 'Checking'
    : sellerProfileIsComplete ? 'Complete' : 'Needs attention'
  const totalCustomerSpend = topCustomers.reduce((total, customer) => total + Number(customer.totalSpend || 0), 0)
  const verifiedCustomerCount = topCustomers.filter(customer => customer.tags.includes('identity_verified')).length
  const averageVisibleCustomerValue = topCustomers.length > 0
    ? totalCustomerSpend / topCustomers.length
    : 0
  const recurringProductCount = recentProducts.filter(product =>
    product.chargingIntervalSeconds !== undefined && product.chargingIntervalSeconds > 0
  ).length
  const trackedProductRows = [...recentProducts]
    .map(product => ({
      product,
      trackedRevenue: productRevenueById[product.id] ?? 0
    }))
    .sort((a, b) => {
      if (b.trackedRevenue !== a.trackedRevenue) {
        return b.trackedRevenue - a.trackedRevenue
      }

      return new Date(b.product.updatedAt).getTime() - new Date(a.product.updatedAt).getTime()
    })
  const visibleTrackedProductRows = trackedProductRows
    .filter(row => row.trackedRevenue > 0)
    .slice(0, 5)
  const maxVisibleTrackedProductRevenue = visibleTrackedProductRows[0]?.trackedRevenue ?? 0
  const trackedProductsCount = trackedProductRows.filter(row => row.trackedRevenue > 0).length
  const totalTrackedProductRevenue = trackedProductRows.reduce((total, row) => total + row.trackedRevenue, 0)
  const topTrackedProduct = trackedProductRows.find(row => row.trackedRevenue > 0)
  const getProductRevenueMeta = (product: IProduct): string => {
    const priceLabel = formatCurrency(Number(product.price || 0), product.currency)

    if (product.chargingIntervalSeconds !== undefined && product.chargingIntervalSeconds > 0) {
      if (product.chargingIntervalSeconds >= 31536000) {
        return `${priceLabel} per year`
      }
      if (product.chargingIntervalSeconds >= 2592000) {
        return `${priceLabel} per month`
      }
      if (product.chargingIntervalSeconds >= 604800) {
        return `${priceLabel} per week`
      }

      return `${priceLabel} recurring`
    }

    return `${priceLabel} one-time`
  }
  const getCustomerDisplayName = (customer: Customer): string =>
    customer.name || customer.companyName || customer.email || 'Customer'
  const getInitials = (value: string): string =>
    value
      .split(' ')
      .filter(Boolean)
      .slice(0, 2)
      .map(part => part[0]?.toUpperCase())
      .join('') || 'C'
  const profileName = sellerProfile?.name || User.organization?.name || 'Seller profile'
  const profileWebsite = sellerProfile?.website || (isSellerProfileLoading ? 'Loading website...' : 'Website missing')
  const compactProfileWebsite = sellerProfile?.website
    ? sellerProfile.website.replace(/^https?:\/\//, '').replace(/\/$/, '')
    : (isSellerProfileLoading ? 'Loading website...' : 'Website missing')
  const profileLogoUrl = sellerProfile?.logoUrl
  const bridgeStatus = User.organization?.bridgeCustomer?.status
  const hasVirtualAccountCapabilities = Object.values(virtualAccountsCapabilities).some(Boolean)
  const virtualAccountsEnabled = hasVirtualAccountCapabilities || virtualAccounts.length > 0
  const fiatPayoutsEnabled = bridgeStatus === 'Accepted'
  const fiatPayoutStatus = fiatPayoutsEnabled
    ? 'Enabled'
    : bridgeStatus === 'Pending' || bridgeStatus === 'TosApproved' || bridgeStatus === 'Created'
      ? 'Pending'
      : 'Not enabled'
  const organizationFeatures = User.organization?.features as Record<string, boolean | undefined> | undefined
  const openBankingEnabled = !!organizationFeatures?.isOpenBankingEnabled
  const getVirtualAccountType = (account: DepositAccount): string => {
    const fiat = account.accountDetails.Fiat?.accountType
    if (fiat?.us) return 'USD'
    if (fiat?.iban) return 'EUR'
    if (fiat?.spei) return 'MXN'
    if (fiat?.pix) return 'BRL'
    return 'Fiat'
  }
  const getVirtualAccountDestination = (account: DepositAccount): string => {
    const fiat = account.accountDetails.Fiat?.accountType
    return fiat?.us?.destinationCurrency ||
      fiat?.iban?.destinationCurrency ||
      fiat?.spei?.destinationCurrency ||
      fiat?.pix?.destinationCurrency ||
      'USDC'
  }
  const getVirtualAccountRails = (account: DepositAccount): string => {
    const fiat = account.accountDetails.Fiat?.accountType
    const rails = fiat?.us?.sourcePaymentRails ||
      fiat?.iban?.sourcePaymentRails ||
      fiat?.spei?.sourcePaymentRails ||
      fiat?.pix?.sourcePaymentRails ||
      []

    return rails.length > 0
      ? rails.map(rail => rail.split('_')[0].toUpperCase()).join(', ')
      : 'Bank transfer'
  }
  const getAssociatedEntityLabel = (entityType?: AssociatedEntityType): string => {
    switch (entityType) {
    case AssociatedEntityType.Invoice:
      return 'Invoice'
    case AssociatedEntityType.PaymentLink:
      return 'Payment link'
    case AssociatedEntityType.DonationLink:
      return 'Donation link'
    case AssociatedEntityType.CheckoutSession:
      return 'Checkout session'
    case AssociatedEntityType.PaymentSession:
      return 'Payment session'
    case AssociatedEntityType.Subscription:
      return 'Subscription'
    case AssociatedEntityType.OpenBanking:
      return 'Open banking'
    default:
      return 'Payment'
    }
  }
  const getRecentActivityItem = (event: RadomEvent): RecentActivityItem | null => {
    switch (event.name) {
    case 'PaymentEvent':
    case 'RecurringPaymentEvent':
    case 'SubscriptionPaymentEvent':
    case 'OpenBanking': {
      const amount = event.paymentSummary?.grossAmount
        ? formatCurrency(Number(event.paymentSummary.grossAmount), event.paymentSummary.currency)
        : `${Number(event.amount).toLocaleString()} ${event.paymentMethod.ticker}`

      return {
        id: `${event.name}-${event.id}`,
        title: event.name === 'OpenBanking' ? 'Open banking payment' : event.label,
        meta: `${getAssociatedEntityLabel(event.associatedEntity?.entityType)} settled via ${event.paymentMethod.ticker}`,
        amountLabel: amount,
        whenLabel: formatDistance(event.date, new Date(), { addSuffix: true }),
        tone: 'green' as const,
        url: event.url || event.associatedEntity?.link || '/transactions'
      }
    }
    case 'Deposit':
      return {
        id: `${event.name}-${event.data.depositId}`,
        title: 'Virtual account deposit',
        meta: 'Fiat deposit received into a managed balance',
        amountLabel: `+${formatCurrency(event.data.fiatGrossAmount || event.data.grossAmount, User.organization?.defaultCurrency ?? 'USD')}`,
        whenLabel: formatDistance(event.date, new Date(), { addSuffix: true }),
        tone: 'green' as const,
        url: event.url || '/virtual_accounts'
      }
    case 'Withdrawal':
      return {
        id: `${event.name}-${event.data.withdrawalRequestId}`,
        title: 'Payout sent',
        meta: `${event.data.destination ? 'Fiat payout or withdrawal completed' : 'Managed balance withdrawal completed'}`,
        amountLabel: `-${formatCurrency(Number(event.data.withdrawalAmount), User.organization?.defaultCurrency ?? 'USD')}`,
        whenLabel: formatDistance(event.date, new Date(), { addSuffix: true }),
        tone: 'red' as const,
        url: event.url || '/payouts'
      }
    case 'Refund':
      return {
        id: `${event.name}-${event.data.withdrawalRequestId}`,
        title: 'Refund issued',
        meta: 'Funds returned to the payer',
        amountLabel: `-${formatCurrency(Number(event.data.withdrawalAmount), User.organization?.defaultCurrency ?? 'USD')}`,
        whenLabel: formatDistance(event.date, new Date(), { addSuffix: true }),
        tone: 'red' as const,
        url: event.url || '/transactions'
      }
    case 'NewSubscription':
      return {
        id: `${event.name}-${event.data.subscriptionId}`,
        title: 'New subscription',
        meta: `${event.managedMethod.ticker} recurring plan created`,
        amountLabel: formatCurrency(Number(event.data.amount), event.data.currency),
        whenLabel: formatDistance(event.date, new Date(), { addSuffix: true }),
        tone: 'blue' as const,
        url: event.url || '/subscriptions'
      }
    case 'SubscriptionCancelled':
      return {
        id: `${event.name}-${event.data.subscriptionId}`,
        title: 'Subscription cancelled',
        meta: `${event.managedMethod.ticker} subscription was cancelled`,
        amountLabel: 'Cancelled',
        whenLabel: formatDistance(event.date, new Date(), { addSuffix: true }),
        tone: 'neutral' as const,
        url: event.url || '/subscriptions'
      }
    case 'SubscriptionExpired':
      return {
        id: `${event.name}-${event.data.subscriptionId}`,
        title: 'Subscription expired',
        meta: `${event.managedMethod.ticker} subscription reached its end`,
        amountLabel: 'Expired',
        whenLabel: formatDistance(event.date, new Date(), { addSuffix: true }),
        tone: 'neutral' as const,
        url: event.url || '/subscriptions'
      }
    default:
      return null
    }
  }
  const recentActivityItems = isQaSeed
    ? QA_RECENT_ACTIVITY
    : Events.allEvents
      .map(getRecentActivityItem)
      .filter((item): item is NonNullable<ReturnType<typeof getRecentActivityItem>> => !!item)
      .slice(0, 6)
  const timeRangeControl = <SegmentedControl aria-label='Time range'>
    {TIME_RANGE_OPTIONS.map(option =>
      <SegmentButton
        key={option.label}
        $active={selectedTimeRangeValue === option.numDays}
        type='button'
        title={option.title}
        onClick={() => {
          if (selectedTimeRangeValue === option.numDays) {
            return
          }
          setSelectedTimeRange(option.title)
          setSelectedTimeRangeValue(option.numDays)
        }}
      >
        {option.label}
      </SegmentButton>
    )}
  </SegmentedControl>
  const chartModeControl = <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
    <SegmentedControlLabel>Chart focus</SegmentedControlLabel>
    <SegmentedControl aria-label='Chart metric'>
      {MAIN_CHART_OPTIONS.map(option =>
        <SegmentButton
          key={option.value}
          $active={mainChartMode === option.value}
          $prominent
          type='button'
          onClick={() => setMainChartMode(option.value)}
        >
          {option.label}
        </SegmentButton>
      )}
    </SegmentedControl>
  </div>

  const mainChartAccent = mainChartMode === 'payments'
    ? CHART_COLORS.payments
    : mainChartMode === 'conversion'
      ? CHART_COLORS.checkout
      : CHART_COLORS.revenue
  const mainChartXAxisLabel = (val: number): string =>
    format(new Date(val), selectedTimeRangeValue > 90 ? 'MMM yyyy' : 'MMM d')
  const mainChartXLabel = (val: number): string => format(new Date(val), 'MMM d, yyyy')
  const renderMetricSummaryItem = (metric: MetricData): React.ReactElement => {
    const hydratedMetric = { ...metric, isLoading: metric.isLoading || (!isQaSeed && Events.isLoading) }
    return <SummaryStripItem key={metric.name}>
      <SummaryLabel>{metric.name}</SummaryLabel>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <SummaryValue>
          {hydratedMetric.isLoading
            ? <LoadingBar style={{ height: 22, width: 82 }} />
            : hydratedMetric.formatLabel(getMetricValue(hydratedMetric))}
        </SummaryValue>
        {!hydratedMetric.isLoading && calculateMetricTrend(hydratedMetric, hydratedMetric.flipTrend)}
      </div>
      <SummaryMeta>
        Previous period {hydratedMetric.formatLabel(getMetricPreviousValue(hydratedMetric))}
      </SummaryMeta>
    </SummaryStripItem>
  }
  const expandedMetricForModal = expandedMetric
    ? {
      ...expandedMetric,
      metric: expandedMetric.isPrimary
        ? { ...primaryChartMetric, isLoading: primaryChartMetric.isLoading || (!isQaSeed && Events.isLoading) }
        : [...revenueMetrics, ...checkoutMetrics, ...productMetrics, ...bankingMetrics, ...subscriptionMetrics]
          .map(metric => ({ ...metric, isLoading: metric.isLoading || (!isQaSeed && Events.isLoading) }))
          .find(metric => metric.name === expandedMetric.metric.name) ?? expandedMetric.metric,
      accentColor: expandedMetric.isPrimary ? mainChartAccent : expandedMetric.accentColor,
      formatXAxisLabelFn: expandedMetric.isPrimary ? mainChartXAxisLabel : expandedMetric.formatXAxisLabelFn,
      formatXLabelFn: expandedMetric.isPrimary ? mainChartXLabel : expandedMetric.formatXLabelFn
    }
    : undefined
  const primaryChartMetricView = {
    ...primaryChartMetric,
    isLoading: primaryChartMetric.isLoading || (!isQaSeed && Events.isLoading)
  }
  const homePulseMetric = {
    ...(grossVolumeMetric ?? primaryChartMetric),
    name: 'Payment volume',
    isLoading: (grossVolumeMetric ?? primaryChartMetric).isLoading || (!isQaSeed && Events.isLoading)
  }
  const pulseSummaryMetrics = [
    {
      label: 'Payment volume',
      value: homePulseMetric.formatLabel(getMetricValue(homePulseMetric)),
      meta: selectedTimeRange,
      isLoading: homePulseMetric.isLoading
    },
    {
      label: 'Net volume',
      value: netVolumeMetric?.formatLabel(getMetricValue(netVolumeMetric)) ?? formatCurrencyNew(0),
      meta: 'Estimated after fees',
      isLoading: !!netVolumeMetric?.isLoading
    },
    {
      label: 'Completed payments',
      value: paymentCountMetric.formatLabel(getMetricValue(paymentCountMetric)),
      meta: selectedTimeRange,
      isLoading: paymentCountMetric.isLoading
    },
    {
      label: 'Conversion',
      value: conversionRateMetric.formatLabel(getMetricValue(conversionRateMetric)),
      meta: 'Checkout completion',
      isLoading: conversionRateMetric.isLoading
    }
  ]
  const isHomePulseLoading = pulseSummaryMetrics.some(metric => metric.isLoading)
  const hasCurrentPaymentPulseData = getMetricValue(paymentCountMetric) > 0 || getMetricValue(homePulseMetric) > 0
  const homePulseChartHeight = isHomePulseLoading || hasCurrentPaymentPulseData ? 96 : 82
  const previousPaymentVolumeLabel = homePulseMetric.formatLabel(getMetricPreviousValue(homePulseMetric))
  const previousCompletedPaymentsLabel = paymentCountMetric.formatLabel(getMetricPreviousValue(paymentCountMetric))

  if (User.isLoading) {
    return <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: 'calc(100vh - 180px)'
    }}>
      <Spinner />
    </div>
  }

  return <Container style={{
    flexDirection: 'column',
    gap: mode === 'home' ? 18 : 22
  }}>

    <VerificationBanner />

    {mode === 'home' && <>
      <MerchantHeader $needsAttention={shouldPromptSellerProfile}>
        <WelcomeProfile>
          <WelcomeLogo $imageUrl={profileLogoUrl}>
            {!profileLogoUrl && getInitials(profileName)}
          </WelcomeLogo>
          <div style={{ minWidth: 0 }}>
            <WelcomeTitleRow>
              <WelcomeTitle>
                {shouldPromptSellerProfile ? 'Complete your merchant profile' : profileName}
              </WelcomeTitle>
              {shouldPromptSellerProfile && <WelcomeStatusRow>
                <SetupStatus $isComplete={false}>{sellerProfileStatus}</SetupStatus>
              </WelcomeStatusRow>}
            </WelcomeTitleRow>
            <WelcomeMetaRow>
              <span title={profileWebsite}>{compactProfileWebsite}</span>
              <span>Default currency {User.organization?.defaultCurrency ?? 'USD'}</span>
              <span>{testMode ? 'Test mode' : 'Live mode'}</span>
            </WelcomeMetaRow>
            {shouldPromptSellerProfile && <WelcomeHelperText>
              Add the seller name, website, and logo customers see on hosted payment pages.
            </WelcomeHelperText>}
          </div>
        </WelcomeProfile>
        <ProfileActionButton type='button' onClick={() => navigate('/branding')}>
          {shouldPromptSellerProfile ? 'Complete profile' : 'Edit profile'}
        </ProfileActionButton>
      </MerchantHeader>

      <OverviewHeaderRow>
        <OverviewHeading>Overview</OverviewHeading>
      </OverviewHeaderRow>

      <HomeHeroGrid>
        <BalanceSectionPanel>
          <BalanceCard>
            <BalanceInfo>
              <BalanceHeadingRow>
                <BalanceHeadingLabel>Balance</BalanceHeadingLabel>
                <BalanceHeadingMeta>({User.organization?.defaultCurrency ?? 'USD'})</BalanceHeadingMeta>
                <Tooltip message='Estimated balance based on the sum of your assets.' />
              </BalanceHeadingRow>
              <BalanceValue>
                {formatHomeBalanceValue(balanceValue)}
              </BalanceValue>
            </BalanceInfo>

            <BalanceAssets>
              <TokenGrid>
                {primaryBalanceAssets.map(asset => {
                  const isMutedAsset = asset.balance !== undefined && asset.balance <= 0

                  return <TokenRow key={asset.token} $muted={isMutedAsset}>
                    <TokenIcon
                      $backgroundImage={Tokens[asset.token]?.logo || getMethodLogoByTicker(asset.token)}
                      $muted={isMutedAsset}
                    />
                    <TokenMeta>
                      <TokenTicker $muted={isMutedAsset}>{asset.token}</TokenTicker>
                      <TokenBalance $muted={isMutedAsset}>
                        {asset.balance === undefined
                          ? <LoadingBar style={{ height: 15, width: 40 }} />
                          : new Intl.NumberFormat('en-US', {
                            maximumFractionDigits: 4,
                            maximumSignificantDigits: 4
                          }).format(asset.balance)}
                      </TokenBalance>
                    </TokenMeta>
                  </TokenRow>
                })}
              </TokenGrid>
            </BalanceAssets>

            <BalanceActionButton
              onClick={() => navigate('/funds')}
              type='button'
            >
              <ActionButtonText>
                <span>Manage funds</span>
                <ActionButtonArrow fill='currentColor' />
              </ActionButtonText>
            </BalanceActionButton>
          </BalanceCard>
        </BalanceSectionPanel>

        <HomePulsePanel>
          <SectionHeader>
            <div>
              <HomeSectionHeading>Payment pulse</HomeSectionHeading>
              <HomeSectionSubtext>
                High-level payment activity for the selected period.
              </HomeSectionSubtext>
            </div>
            <ControlRow>
              {timeRangeControl}
              <SetupAction type='button' onClick={() => navigate(getAnalyticsUrl())}>
                <ActionButtonText>
                  <span>Open analytics</span>
                  <ActionButtonArrow fill='currentColor' />
                </ActionButtonText>
              </SetupAction>
            </ControlRow>
          </SectionHeader>

          <PulsePanelBody>
            <PulseChartBlock $hasContent={isHomePulseLoading || hasCurrentPaymentPulseData}>
              <MetricChart
                metric={homePulseMetric}
                height={homePulseChartHeight}
                compact
                marginTop={0}
                accentColor={CHART_COLORS.activity}
                formatXAxisLabelFn={mainChartXAxisLabel}
                formatXLabelFn={mainChartXLabel}
              />
              {!isHomePulseLoading && !hasCurrentPaymentPulseData && <PulseChartEmptyState>
                No payment activity in this period.
              </PulseChartEmptyState>}
            </PulseChartBlock>

            <PulseMetricStrip>
              {pulseSummaryMetrics.map(metric =>
                <PulseMetricStripItem key={metric.label}>
                  <PulseMetricLabel>{metric.label}</PulseMetricLabel>
                  <PulseMetricValue>
                    {metric.isLoading
                      ? <LoadingBar style={{ height: 18, width: 82 }} />
                      : metric.value}
                  </PulseMetricValue>
                  <PulseMetricMeta>{metric.meta}</PulseMetricMeta>
                </PulseMetricStripItem>
              )}
            </PulseMetricStrip>

            <PulseContextCard>
              {isHomePulseLoading
                ? <div style={{ width: '100%' }}>
                  <PulseContextEyebrow>Loading</PulseContextEyebrow>
                  <LoadingBar style={{ height: 14, marginTop: 6, width: '100%' }} />
                </div>
                  : hasCurrentPaymentPulseData
                  ? <div>
                    <PulseContextText>
                      <PulseContextEyebrow as='span'>Previous period</PulseContextEyebrow>
                      {': '}
                      {previousPaymentVolumeLabel} payment volume and {previousCompletedPaymentsLabel} completed payments.
                    </PulseContextText>
                  </div>
                  : <div>
                    <PulseContextText>
                      <PulseContextEyebrow as='span'>Previous period</PulseContextEyebrow>
                      {': '}
                      No settled payments in the selected window yet.
                    </PulseContextText>
                  </div>}
              {!isHomePulseLoading && hasCurrentPaymentPulseData && calculateMetricTrend(homePulseMetric)}
            </PulseContextCard>
          </PulsePanelBody>
        </HomePulsePanel>
      </HomeHeroGrid>

      <HomeSecondaryGrid>
        <SectionPanel>
          <SectionHeader>
            <div>
              <SectionTitle>Recent activity</SectionTitle>
              <SectionSubtext>Latest payments, deposits, payouts, and subscription events.</SectionSubtext>
            </div>
            <SetupAction type='button' onClick={() => navigate('/transactions')}>
              View transactions
            </SetupAction>
          </SectionHeader>

          {!isQaSeed && Events.isLoading
            ? <LoadingBar style={{ height: 132, width: '100%' }} />
            : recentActivityItems.length > 0
              ? <ActivityList>
                {recentActivityItems.map(item =>
                  <ActivityRow key={item.id} type='button' onClick={() => navigate(item.url)}>
                    <ActivityBadge $tone={item.tone}>{item.title[0]}</ActivityBadge>
                    <div style={{ minWidth: 0 }}>
                      <ActivityTitle>{item.title}</ActivityTitle>
                      <ActivityMeta>{item.meta}</ActivityMeta>
                    </div>
                    <ActivityAside>
                      <ActivityAmount $tone={item.tone}>{item.amountLabel}</ActivityAmount>
                      <ActivityWhen>{item.whenLabel}</ActivityWhen>
                    </ActivityAside>
                  </ActivityRow>
                )}
              </ActivityList>
              : <HomeActivityEmptyState>
                Activity will appear here as payments, deposits, payouts, and subscription events arrive.
              </HomeActivityEmptyState>}
        </SectionPanel>

      </HomeSecondaryGrid>
    </>}

    {mode === 'analytics' && <>
      <SectionPanel>
        <SectionHeader>
          <div>
            <SectionTitle>Analytics</SectionTitle>
          </div>
          <SetupAction type='button' onClick={() => navigate(`/${isQaSeed ? '?qa=1' : ''}`)}>
            Back to home
          </SetupAction>
        </SectionHeader>
        <AnalyticsTopBar>
          <HomeTabs aria-label='Dashboard sections' role='tablist'>
            {DASHBOARD_TABS.map(tab =>
              <HomeTabButton
                key={tab.value}
                $active={activeTab === tab.value}
                role='tab'
                aria-selected={activeTab === tab.value}
                type='button'
                onClick={() => handleAnalyticsTabChange(tab.value)}
              >
                {tab.label}
              </HomeTabButton>
            )}
          </HomeTabs>
          <ControlRow>
            {timeRangeControl}
          </ControlRow>
        </AnalyticsTopBar>
      </SectionPanel>
    </>}

    {mode === 'analytics' && activeTab === 'overview' && <>
      <SectionPanel>
        <SectionHeader>
          <div>
            <SectionTitle>Payments overview</SectionTitle>
          </div>
          <ControlRow>
            {chartModeControl}
          </ControlRow>
        </SectionHeader>
        <MainMetricCard>
          <MetricCardView
            metric={primaryChartMetricView}
            large
            chartHeight={310}
            showYAxis
            accentColor={mainChartAccent}
            formatXAxisLabelFn={mainChartXAxisLabel}
            formatXLabelFn={mainChartXLabel}
            onExpand={metric => setExpandedMetric({
              metric,
              accentColor: mainChartAccent,
              formatXAxisLabelFn: mainChartXAxisLabel,
              formatXLabelFn: mainChartXLabel,
              isPrimary: true
            })}
          />
        </MainMetricCard>
        <SummaryStrip style={{ gridTemplateColumns: 'repeat(5, minmax(0, 1fr))' }}>
          <SummaryStripItem>
            <SummaryLabel>{selectedTimeRange} gross</SummaryLabel>
            <SummaryValue>
              {grossVolumeMetric?.formatLabel(getMetricValue(grossVolumeMetric)) ?? formatCurrencyNew(0)}
            </SummaryValue>
            <SummaryMeta>Before fees and adjustments</SummaryMeta>
          </SummaryStripItem>
          <SummaryStripItem>
            <SummaryLabel>{selectedTimeRange} net</SummaryLabel>
            <SummaryValue>
              {netVolumeMetric?.formatLabel(getMetricValue(netVolumeMetric)) ?? formatCurrencyNew(0)}
            </SummaryValue>
            <SummaryMeta>Estimated after Radom fees</SummaryMeta>
          </SummaryStripItem>
          <SummaryStripItem>
            <SummaryLabel>Payments</SummaryLabel>
            <SummaryValue>{totalPaymentsInRange.toString()}</SummaryValue>
            <SummaryMeta>One-time and recurring payments</SummaryMeta>
          </SummaryStripItem>
          <SummaryStripItem>
            <SummaryLabel>Average payment</SummaryLabel>
            <SummaryValue>{averagePaymentSizeLabel}</SummaryValue>
            <SummaryMeta>Gross revenue divided by paid events</SummaryMeta>
          </SummaryStripItem>
          <SummaryStripItem>
            <SummaryLabel>Checkout conversion</SummaryLabel>
            <SummaryValue>{checkoutConversionLabel}</SummaryValue>
            <SummaryMeta>Crypto and open banking average</SummaryMeta>
          </SummaryStripItem>
        </SummaryStrip>
      </SectionPanel>

    </>}

    {mode === 'analytics' && activeTab === 'payments' && <>
      <SectionPanel>
        <SectionHeader>
          <div>
            <SectionTitle>Payments performance</SectionTitle>
          </div>
        </SectionHeader>

        <AnalyticsMetricsSection>
          <AnalyticsMetricsGroup>
            <AnalyticsGroupLabel>Payment activity</AnalyticsGroupLabel>
            <SummaryStrip style={{ gridTemplateColumns: 'repeat(4, minmax(0, 1fr))' }}>
              {revenueMetrics.map(metric => renderMetricSummaryItem(metric))}
            </SummaryStrip>
          </AnalyticsMetricsGroup>

          <AnalyticsMetricsGroup>
            <AnalyticsGroupLabel>Checkout performance</AnalyticsGroupLabel>
            <SummaryStrip style={{ gridTemplateColumns: 'repeat(4, minmax(0, 1fr))' }}>
              {checkoutMetrics.map(metric => renderMetricSummaryItem(metric))}
            </SummaryStrip>
          </AnalyticsMetricsGroup>
        </AnalyticsMetricsSection>
      </SectionPanel>
    </>}

    {mode === 'analytics' && activeTab === 'products' && <>
      <SectionPanel>
        <SectionHeader>
          <div>
            <SectionTitle>Product performance</SectionTitle>
          </div>
          <SetupAction type='button' onClick={() => navigate('/products')}>
            Manage products
          </SetupAction>
        </SectionHeader>

        <SummaryStrip style={{ gridTemplateColumns: 'repeat(4, minmax(0, 1fr))' }}>
          <SummaryStripItem>
            <SummaryLabel>Catalog products</SummaryLabel>
            <SummaryValue>
              {isProductsLoading ? <LoadingBar style={{ height: 24, width: 56 }} /> : recentProducts.length}
            </SummaryValue>
            <SummaryMeta>Active products available to merchants</SummaryMeta>
          </SummaryStripItem>
          <SummaryStripItem>
            <SummaryLabel>Revenue generated</SummaryLabel>
            <SummaryValue>
              {isProductsLoading || isProductRevenueLoading
                ? <LoadingBar style={{ height: 24, width: 96 }} />
                : formatCurrency(totalTrackedProductRevenue, User.organization?.defaultCurrency ?? 'USD')}
            </SummaryValue>
            <SummaryMeta>Tracked across invoices, links, sessions, and subscriptions</SummaryMeta>
          </SummaryStripItem>
          <SummaryStripItem>
            <SummaryLabel>Products generating revenue</SummaryLabel>
            <SummaryValue>
              {isProductsLoading || isProductRevenueLoading
                ? <LoadingBar style={{ height: 24, width: 56 }} />
                : trackedProductsCount}
            </SummaryValue>
            <SummaryMeta>Products with tracked revenue in this range</SummaryMeta>
          </SummaryStripItem>
          <SummaryStripItem>
            <SummaryLabel>Top product</SummaryLabel>
            <SummaryValue>
              {isProductsLoading || isProductRevenueLoading
                ? <LoadingBar style={{ height: 24, width: 120 }} />
                : topTrackedProduct?.product.name ?? 'No revenue yet'}
            </SummaryValue>
            <SummaryMeta>
              {topTrackedProduct
                ? formatCurrency(topTrackedProduct.trackedRevenue, User.organization?.defaultCurrency ?? 'USD')
                : `${recurringProductCount} recurring products in catalog`}
            </SummaryMeta>
          </SummaryStripItem>
        </SummaryStrip>

        <div style={{ marginTop: 18 }}>
          {(isProductsLoading || isProductRevenueLoading)
            ? <LoadingBar style={{ height: 90, width: '100%' }} />
            : visibleTrackedProductRows.length > 0
              ? <>
                <EntityList>
                  <EntityListHeader>
                    <span />
                    <span>Product</span>
                    <span>Revenue generated</span>
                  </EntityListHeader>
                  {visibleTrackedProductRows.map(({ product, trackedRevenue }) =>
                    <EntityRow key={product.id} type='button' onClick={() => navigate(`/products/${product.id}`)}>
                      <EntityAvatar $imageUrl={product.imageUrl}>
                        {!product.imageUrl && product.name[0]?.toUpperCase()}
                      </EntityAvatar>
                      <div style={{ minWidth: 0 }}>
                        <EntityTitle>
                          {product.name}
                          {product.chargingIntervalSeconds !== undefined && product.chargingIntervalSeconds > 0 &&
                            <InlinePill $tone='green'>Recurring</InlinePill>}
                        </EntityTitle>
                        <EntitySubtext>
                          {getProductRevenueMeta(product)} · Updated {format(new Date(product.updatedAt), 'MMM d, yyyy')}
                        </EntitySubtext>
                      </div>
                      <EntityValue>
                        <EntityValueLabel>Revenue generated</EntityValueLabel>
                        {formatCurrency(trackedRevenue, User.organization?.defaultCurrency ?? 'USD')}
                      </EntityValue>
                    </EntityRow>
                  )}
                </EntityList>

                <ProductRevenueChart>
                  <ProductRevenueChartHeader>
                    <ProductRevenueChartTitle>Top products by revenue</ProductRevenueChartTitle>
                    <ProductRevenueChartHint>Current reporting window.</ProductRevenueChartHint>
                  </ProductRevenueChartHeader>
                  <ProductRevenueChartRows>
                    {visibleTrackedProductRows.map(({ product, trackedRevenue }) => (
                      <ProductRevenueChartRow key={`${product.id}-chart`}>
                        <ProductRevenueChartName>{product.name}</ProductRevenueChartName>
                        <ProductRevenueChartTrack>
                          <ProductRevenueChartFill
                            $width={maxVisibleTrackedProductRevenue > 0
                              ? Math.max(12, (trackedRevenue / maxVisibleTrackedProductRevenue) * 100)
                              : 0}
                          />
                        </ProductRevenueChartTrack>
                        <ProductRevenueChartValue>
                          {formatCurrency(trackedRevenue, User.organization?.defaultCurrency ?? 'USD')}
                        </ProductRevenueChartValue>
                      </ProductRevenueChartRow>
                    ))}
                  </ProductRevenueChartRows>
                </ProductRevenueChart>
              </>
              : <EmptySection>
                No product revenue tracked in this window yet.
              </EmptySection>}
        </div>
      </SectionPanel>

      {hasProductTabData
        ? <>
          {visibleProductMetrics.length > 0 && <SectionPanel>
            <SectionHeader>
              <div>
                <SectionTitle>Revenue by payment surface</SectionTitle>
              </div>
            </SectionHeader>
            <SummaryStrip style={{ gridTemplateColumns: 'repeat(5, minmax(0, 1fr))' }}>
              {visibleProductMetrics.map(metric => renderMetricSummaryItem(metric))}
            </SummaryStrip>
          </SectionPanel>}

          {visibleSubscriptionMetrics.length > 0 && <SectionPanel>
            <SectionHeader>
              <div>
                <SectionTitle>Subscription health</SectionTitle>
              </div>
            </SectionHeader>
            <SummaryStrip style={{ gridTemplateColumns: 'repeat(4, minmax(0, 1fr))' }}>
              {visibleSubscriptionMetrics.map(metric => renderMetricSummaryItem(metric))}
            </SummaryStrip>
          </SectionPanel>}
        </>
        : <EmptySection>
          No product or subscription activity for this period yet.
          Product and subscription analytics will appear here once payments are attributed to those surfaces.
        </EmptySection>}
    </>}

    {mode === 'analytics' && activeTab === 'customers' && <>
      <SectionPanel>
        <SectionHeader>
          <div>
            <SectionTitle>Customer value</SectionTitle>
          </div>
          <SetupAction type='button' onClick={() => navigate('/customers')}>
            View customers
          </SetupAction>
        </SectionHeader>

        <SummaryStrip style={{ gridTemplateColumns: 'repeat(4, minmax(0, 1fr))' }}>
          <SummaryStripItem>
            <SummaryLabel>Total customers</SummaryLabel>
            <SummaryValue>
              {isCustomersLoading ? <LoadingBar style={{ height: 24, width: 56 }} /> : totalCustomerCount}
            </SummaryValue>
            <SummaryMeta>From the customer directory</SummaryMeta>
          </SummaryStripItem>
          <SummaryStripItem>
            <SummaryLabel>Visible top spend</SummaryLabel>
            <SummaryValue>
              {isCustomersLoading
                ? <LoadingBar style={{ height: 24, width: 76 }} />
                : formatCurrency(totalCustomerSpend, User.organization?.defaultCurrency ?? 'USD')}
            </SummaryValue>
            <SummaryMeta>Top customers shown below</SummaryMeta>
          </SummaryStripItem>
          <SummaryStripItem>
            <SummaryLabel>Average visible value</SummaryLabel>
            <SummaryValue>
              {isCustomersLoading
                ? <LoadingBar style={{ height: 24, width: 76 }} />
                : formatCurrency(averageVisibleCustomerValue, User.organization?.defaultCurrency ?? 'USD')}
            </SummaryValue>
            <SummaryMeta>Top customer average</SummaryMeta>
          </SummaryStripItem>
          <SummaryStripItem>
            <SummaryLabel>KYC verified</SummaryLabel>
            <SummaryValue>
              {isCustomersLoading ? <LoadingBar style={{ height: 24, width: 56 }} /> : verifiedCustomerCount}
            </SummaryValue>
            <SummaryMeta>Among visible top customers</SummaryMeta>
          </SummaryStripItem>
        </SummaryStrip>

        <div style={{ marginTop: 18 }}>
          {isCustomersLoading
            ? <LoadingBar style={{ height: 120, width: '100%' }} />
            : topCustomers.length > 0
              ? <EntityList>
                <EntityListHeader>
                  <span />
                  <span>Top customer</span>
                  <span>Total customer revenue</span>
                </EntityListHeader>
                {topCustomers.map(customer =>
                  <EntityRow key={customer.id} type='button' onClick={() => navigate(`/customer/${customer.id}`)}>
                    <EntityAvatar>
                      {getInitials(getCustomerDisplayName(customer))}
                    </EntityAvatar>
                    <div style={{ minWidth: 0 }}>
                      <EntityTitle>
                        {getCustomerDisplayName(customer)}
                        {customer.tags.includes('identity_verified') && <InlinePill $tone='green'>KYC</InlinePill>}
                      </EntityTitle>
                      <EntitySubtext>
                        {customer.email || customer.accountType || 'Customer record'}
                      </EntitySubtext>
                    </div>
                    <EntityValue>
                      <EntityValueLabel>Total customer revenue</EntityValueLabel>
                      {formatCurrency(Number(customer.totalSpend || 0), User.organization?.defaultCurrency ?? 'USD')}
                    </EntityValue>
                  </EntityRow>
                )}
              </EntityList>
              : <EmptySection>
                No customers with spend yet.
                Customer value analytics will become more useful once payments are linked to customer records.
              </EmptySection>}
        </div>
      </SectionPanel>
    </>}

    {mode === 'analytics' && activeTab === 'banking' && <>
      <SectionPanel>
        <SectionHeader>
          <div>
            <SectionTitle>Banking setup</SectionTitle>
          </div>
          <SetupAction type='button' onClick={() => navigate('/virtual_accounts')}>
            Manage virtual accounts
          </SetupAction>
        </SectionHeader>

        <SummaryStrip style={{ gridTemplateColumns: 'repeat(4, minmax(0, 1fr))' }}>
          <SummaryStripItem>
            <SummaryLabel>Open banking checkout</SummaryLabel>
            <SummaryValue style={{ fontSize: 14, lineHeight: 1.2 }}>
              <SetupStatus $isComplete={openBankingEnabled}>
                {openBankingEnabled ? 'Enabled' : 'Not enabled'}
              </SetupStatus>
            </SummaryValue>
            <SummaryMeta>Bank payment checkout rail</SummaryMeta>
          </SummaryStripItem>
          <SummaryStripItem>
            <SummaryLabel>On-ramp</SummaryLabel>
            <SummaryValue style={{ fontSize: 14, lineHeight: 1.2 }}>
              <SetupStatus $isComplete={virtualAccountsEnabled}>
                {virtualAccountsEnabled ? 'Enabled' : fiatPayoutStatus === 'Pending' ? 'Pending' : 'Not enabled'}
              </SetupStatus>
            </SummaryValue>
            <SummaryMeta>Virtual accounts for fiat deposits</SummaryMeta>
          </SummaryStripItem>
          <SummaryStripItem>
            <SummaryLabel>Off-ramp</SummaryLabel>
            <SummaryValue style={{ fontSize: 14, lineHeight: 1.2 }}>
              <SetupStatus $isComplete={fiatPayoutsEnabled}>
                {fiatPayoutStatus}
              </SetupStatus>
            </SummaryValue>
            <SummaryMeta>Fiat payouts to bank accounts</SummaryMeta>
          </SummaryStripItem>
          <SummaryStripItem>
            <SummaryLabel>Virtual accounts</SummaryLabel>
            <SummaryValue>
              {isVirtualAccountsLoading
                ? <LoadingBar style={{ height: 24, width: 56 }} />
                : virtualAccounts.length}
            </SummaryValue>
            <SummaryMeta>Active fiat deposit accounts</SummaryMeta>
          </SummaryStripItem>
        </SummaryStrip>

        <div style={{ marginTop: 18 }}>
          {isVirtualAccountsLoading
            ? <LoadingBar style={{ height: 96, width: '100%' }} />
            : virtualAccounts.length > 0
              ? <EntityList>
                <EntityListHeader>
                  <span />
                  <span>Virtual account</span>
                  <span>Destination</span>
                </EntityListHeader>
                {virtualAccounts.map(account =>
                  <EntityRow key={account.id} type='button' onClick={() => navigate('/virtual_accounts')}>
                    <EntityAvatar>{getVirtualAccountType(account)}</EntityAvatar>
                    <div style={{ minWidth: 0 }}>
                      <EntityTitle>
                        {account.name}
                        <InlinePill $tone={account.status === 'active' ? 'green' : 'neutral'}>
                          {account.status}
                        </InlinePill>
                      </EntityTitle>
                      <EntitySubtext>
                        {getVirtualAccountType(account)} deposits via {getVirtualAccountRails(account)}
                      </EntitySubtext>
                    </div>
                    <EntityValue>
                      <EntityValueLabel>Destination</EntityValueLabel>
                      {getVirtualAccountDestination(account)}
                    </EntityValue>
                  </EntityRow>
                )}
              </EntityList>
              : <EmptySection>
                No virtual accounts yet. Use virtual accounts to receive fiat deposits into crypto balances.
              </EmptySection>}
        </div>
      </SectionPanel>

      {hasBankingTabData
        ? <SectionPanel>
          <SectionHeader>
            <div>
              <SectionTitle>Banking rails</SectionTitle>
            </div>
          </SectionHeader>
          <SummaryStrip style={{ gridTemplateColumns: 'repeat(4, minmax(0, 1fr))' }}>
            {visibleBankingMetrics.map(metric => renderMetricSummaryItem(metric))}
          </SummaryStrip>
        </SectionPanel>
        : <EmptySection>
          No banking rail activity for this period yet.
          Open banking, virtual account, and payout analytics will appear here when those products are used.
        </EmptySection>}
    </>}

    {mode === 'analytics' && <Modal
      visible={!!expandedMetricForModal}
      onClose={() => setExpandedMetric(undefined)}
      title={expandedMetricForModal?.metric.name}
      innerStyles={{
        borderRadius: 14,
        maxWidth: 'calc(100vw - 32px)',
        overflow: 'hidden',
        width: 'min(980px, calc(100vw - 32px))'
      }}
    >
      {expandedMetricForModal && <div>
        <ExpandedChartControls>
          <div>
            <AnalyticsToolbarTitle>Chart range</AnalyticsToolbarTitle>
            <AnalyticsToolbarSubtext>Updates the enlarged chart and dashboard view.</AnalyticsToolbarSubtext>
          </div>
          <ControlRow>
            {expandedMetricForModal.isPrimary && chartModeControl}
            {timeRangeControl}
          </ControlRow>
        </ExpandedChartControls>
        <MetricCardView
          metric={expandedMetricForModal.metric}
          large
          chartHeight={430}
          showYAxis
          accentColor={expandedMetricForModal.accentColor}
          formatXAxisLabelFn={expandedMetricForModal.formatXAxisLabelFn}
          formatXLabelFn={expandedMetricForModal.formatXLabelFn}
        />
      </div>}
    </Modal>}
  </Container>
})

export default Home
